import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import logo from './logo.png';
import {
  getPhaseInfo,
  getWeeklyNote,
  PREMIUM_TIPS,
  detectStreakMilestones,
  isoWeek,
} from './insights';
import WeightTrendChart from './components/WeightTrendChart';
import {
  Home,
  Camera,
  Pill,
  Droplet,
  Scale,
  Smile,
  Lightbulb,
  Info,
  Gem,
  Heart,
  TrendingUp,
  FileText,
  GraduationCap,
  Target,
  BarChart3,
  Check,
  X,
  AlertTriangle,
  Award,
  Sparkles,
  Share2,
  Download,
  Snowflake,
  Calendar,
  Trophy,
  ExternalLink,
  ShoppingBag,
} from 'lucide-react';

// PWA-friendly backend URL configuration
const getBackendURL = () => {
  // PRODUCTION CHECK FIRST - For production deployment on yourgastricbuddy.com
  if (window.location.hostname === 'yourgastricbuddy.com' || 
      window.location.hostname === 'www.yourgastricbuddy.com') {
    return 'https://gastricbuddy.emergent.host'; // Production backend URL
  }
  
  // Use environment variable for local development only
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Fallback to current origin for local development
  return window.location.origin;
};

const BACKEND_URL = getBackendURL();

function App() {
  // Analytics label prettifier — turns backend enum strings into human copy
  const prettyAnalyticsValue = (v, fallback = '—') => {
    if (v === null || v === undefined || v === '') return fallback;
    if (typeof v === 'number') return v;
    const map = {
      insufficient_data: 'Not enough data yet',
      goal_achieved_or_rate_insufficient: 'Keep logging to project a date',
      losing: 'Losing weight',
      gaining: 'Gaining weight',
      stable: 'Holding steady',
      start_tracking_meals: 'meal tracking consistency',
      analyzing: 'Analyzing...',
    };
    const key = String(v).toLowerCase();
    if (map[key]) return map[key];
    // Fallback: replace underscores, title-case
    return String(v)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const [activeTab, setActiveTab] = useState('home');
  const [userProfile, setUserProfile] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [foodHistory, setFoodHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [camera, setCamera] = useState({ active: false, stream: null });
  const [capturedImage, setCapturedImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showSplash, setShowSplash] = useState(() => {
    try {
      return sessionStorage.getItem('gb_splash_shown') !== '1';
    } catch (e) {
      return true;
    }
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    age: '',
    gender: 'female',
    height_feet: '',
    height_inches: '',
    current_weight_lbs: '',
    goal_weight_lbs: '',
    activity_level: 'light',
    surgery_date: ''
  });

  // Weight tracking state
  const [weightHistory, setWeightHistory] = useState([]);
  const [newWeightEntry, setNewWeightEntry] = useState({
    weight_lbs: '',
    notes: ''
  });

  // Vitamin tracking state
  const [vitaminHistory, setVitaminHistory] = useState([]);
  const [newVitaminEntry, setNewVitaminEntry] = useState({
    vitamin_name: '',
    dosage_mg: '',
    time_taken: 'morning',
    notes: ''
  });

  // Fluid tracking state
  const [fluidSummary, setFluidSummary] = useState(null);
  const [newFluidEntry, setNewFluidEntry] = useState({
    fluid_type: 'water',
    amount_oz: ''
  });

  // Health tips state
  const [healthTips, setHealthTips] = useState(null);
  const [recommendedVitamins, setRecommendedVitamins] = useState(null);
  const [aboutCreator, setAboutCreator] = useState(null);

  // Legal documents state
  const [privacyPolicy, setPrivacyPolicy] = useState(null);
  const [termsOfService, setTermsOfService] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);

  // Subscription state
  const [subscriptionPackages, setSubscriptionPackages] = useState(null);
  const [userPremiumStatus, setUserPremiumStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [promoValidating, setPromoValidating] = useState(false);
  
  // Premium content state
  const [premiumAnalytics, setPremiumAnalytics] = useState(null);
  const [premiumMealPlans, setPremiumMealPlans] = useState(null);
  const [premiumEducation, setPremiumEducation] = useState(null);
  const [showPremiumContent, setShowPremiumContent] = useState(false);

  // Scan limits state
  const [scanStatus, setScanStatus] = useState({
    scans_used: 0,
    total_limit: 3,
    scans_remaining: 3,
    is_premium: false
  });

  // Mood tracking state
  const [moodHistory, setMoodHistory] = useState([]);
  const [dailyEncouragement, setDailyEncouragement] = useState(null);
  const [newMoodEntry, setNewMoodEntry] = useState({
    mood_rating: 5,
    mood_description: '',
    energy_level: 5,
    journal_entry: '',
    gratitude_note: '',
    challenges: '',
    victories: ''
  });

  // Premium feature state
  const [premiumData, setPremiumData] = useState({
    analytics: null,
    mealPlans: null,
    educationalContent: null,
    lastCheckin: null
  });
  const [checkinMessage, setCheckinMessage] = useState('');
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('30_days');
  const [reportLoading, setReportLoading] = useState(false);

  // Insights (streaks, weekly note, chart window) — Home tab
  const [streaks, setStreaks] = useState(null);
  const [freezeStatus, setFreezeStatus] = useState(null);
  const [freezing, setFreezing] = useState(false);
  const [chartWindow, setChartWindow] = useState(7); // 7 | 30 | 60 | 90
  const [sharingCard, setSharingCard] = useState(false);
  const [sharePreview, setSharePreview] = useState({ open: false, dataUrl: null, blob: null });
  const [streakToast, setStreakToast] = useState({ show: false, events: [] });
  const [weeklyDigest, setWeeklyDigest] = useState({ open: false });
  const [milestones, setMilestones] = useState([]);
  const shareCanvasRef = useRef(null);
  
  // Profile update state
  const [showProfileUpdate, setShowProfileUpdate] = useState(false);

  // Profile restoration utility with session token support
  const restoreProfileFromStorage = () => {
    // Try multiple storage methods for PWA compatibility
    const storageKeys = [
      'gastric_buddy_user_id',
      'gastric_buddy_profile',
      'gb_user_id' // Backup key
    ];
    
    const emailKeys = [
      'gastric_buddy_email', 
      'gastric_buddy_user_email',
      'gb_email' // Backup key
    ];
    
    const codeKeys = [
      'gastric_buddy_recovery_code',
      'gb_recovery_code' // Backup key
    ];
    
    let userId = null;
    let email = null;
    let recoveryCode = null;
    
    // Try to find user ID from any storage key
    for (const key of storageKeys) {
      const stored = localStorage.getItem(key);
      if (stored) {
        if (key === 'gastric_buddy_profile') {
          try {
            const profile = JSON.parse(stored);
            userId = profile.user_id;
            email = profile.email;
            recoveryCode = profile.recovery_code;
            break;
          } catch (e) {
            console.log(`Error parsing ${key}:`, e);
          }
        } else {
          userId = stored;
        }
      }
    }
    
    // Try to find email from any storage key
    if (!email) {
      for (const key of emailKeys) {
        const stored = localStorage.getItem(key);
        if (stored) {
          email = stored;
          break;
        }
      }
    }
    
    // Try to find recovery code from any storage key
    if (!recoveryCode) {
      for (const key of codeKeys) {
        const stored = localStorage.getItem(key);
        if (stored) {
          recoveryCode = stored;
          break;
        }
      }
    }
    
    // Final fallback: try session storage
    if (!userId || !recoveryCode) {
      try {
        if (!userId) userId = sessionStorage.getItem('gb_backup_user');
        if (!email) email = sessionStorage.getItem('gb_backup_email');
        if (!recoveryCode) recoveryCode = sessionStorage.getItem('gb_backup_code');
      } catch (e) {
        console.log('Session storage fallback failed:', e);
      }
    }
    
    return { userId, email, recoveryCode };
  };

  // Enhanced profile persistence with recovery code support  
  const persistProfile = (userId, email, profileData = null, recoveryCode = null) => {
    try {
      // Store user data in multiple keys for PWA reliability
      localStorage.setItem('gastric_buddy_user_id', userId);
      localStorage.setItem('gb_user_id', userId); // Backup
      
      if (email) {
        localStorage.setItem('gastric_buddy_email', email);
        localStorage.setItem('gb_email', email); // Backup
      }
      
      // Store recovery code for PWA access
      if (recoveryCode) {
        localStorage.setItem('gastric_buddy_recovery_code', recoveryCode);
        localStorage.setItem('gb_recovery_code', recoveryCode); // Backup
      }
      
      if (profileData) {
        const profileObj = {
          user_id: userId,
          email: email,
          recovery_code: recoveryCode,
          ...profileData,
          last_saved: new Date().toISOString()
        };
        localStorage.setItem('gastric_buddy_profile', JSON.stringify(profileObj));
      }
      
      // Also store critical data in session storage for extra PWA reliability
      try {
        sessionStorage.setItem('gb_backup_user', userId);
        if (email) sessionStorage.setItem('gb_backup_email', email);
        if (recoveryCode) sessionStorage.setItem('gb_backup_code', recoveryCode);
      } catch (e) {
        console.log('Session storage not available:', e);
      }
      
      console.log('✅ Profile persisted with recovery code for PWA compatibility');
    } catch (error) {
      console.error('❌ Error persisting profile:', error);
    }
  };

  // Load user data on component mount
  // PWA Install Prompt Handler
  useEffect(() => {
    // Listen for PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  // Splash screen — show once per session, ~1.7s total
  useEffect(() => {
    if (!showSplash) return;
    const timer = setTimeout(() => {
      setShowSplash(false);
      try { sessionStorage.setItem('gb_splash_shown', '1'); } catch (e) {}
    }, 1700);
    return () => clearTimeout(timer);
  }, [showSplash]);

  useEffect(() => {
    console.log('🔄 App starting, checking localStorage for user session...');
    console.log('🌐 Current hostname:', window.location.hostname);
    console.log('🔗 Using backend URL:', BACKEND_URL);
    console.log('📱 User agent:', navigator.userAgent);
    
    // Check if we're in a PWA context
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone === true ||
                  window.location.search.includes('source=pwa');
    console.log('📱 PWA mode:', isPWA);
    
    // Try to restore profile from multiple storage locations
    const { userId: savedUserId, email: savedEmail, recoveryCode: savedRecoveryCode } = restoreProfileFromStorage();
    console.log('📦 Profile restoration result:', { savedUserId, savedEmail, recoveryCode: savedRecoveryCode });
    
    // Debug: Show all localStorage keys related to profile
    console.log('🔍 All localStorage keys:');
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('gastric') || key.includes('gb_'))) {
        console.log(`- ${key}: ${localStorage.getItem(key)}`);
      }
    }
    
    // Also check URL parameters for profile restoration
    const urlParams = new URLSearchParams(window.location.search);
    const urlUserId = urlParams.get('user_id');
    const urlEmail = urlParams.get('email');
    const restoreCode = urlParams.get('restore');
    
    // Quick profile recreation if recreate parameter is provided
    const recreateProfile = urlParams.get('recreate');
    if (recreateProfile === 'denise' && !savedUserId) {
      console.log('🚀 Quick profile recreation for Denise');
      
      const profileData = {
        user_id: '67e10c68-b56b-4ed6-8f5e-1c241779c1a8',
        name: 'LeTherese Meadows',
        email: 'letheresemeadows@gmail.com',
        age: 45,
        gender: 'female',
        height_feet: 5,
        height_inches: 4,
        current_weight_lbs: 188.0,
        goal_weight_lbs: 160.0,
        activity_level: 'light',
        surgery_date: '2025-03-18',
        recovery_code: '512439'
      };
      
      // Store profile data immediately
      localStorage.setItem('gastric_buddy_user_id', profileData.user_id);
      localStorage.setItem('gastric_buddy_email', profileData.email);
      localStorage.setItem('gastric_buddy_recovery_code', profileData.recovery_code);
      localStorage.setItem('gb_user_id', profileData.user_id);
      localStorage.setItem('gb_email', profileData.email);
      localStorage.setItem('gb_recovery_code', profileData.recovery_code);
      localStorage.setItem('gastric_buddy_profile', JSON.stringify(profileData));
      
      // Set profile immediately
      setUserProfile(profileData);
      
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      
      alert('Profile recreated successfully! Welcome back, LeTherese!');
      return; // Stop further processing
    }
    
    // Auto-restore profile if restore parameter is provided
    if (restoreCode && !savedUserId) {
      console.log('🔄 Auto-restore detected with code:', restoreCode);
      try {
        fetch(`${BACKEND_URL}/api/profile-by-code/${restoreCode}`)
          .then(response => response.json())
          .then(profileData => {
            console.log('✅ Auto-restore successful:', profileData.name);
            
            // Store profile data
            localStorage.setItem('gastric_buddy_user_id', profileData.user_id);
            localStorage.setItem('gastric_buddy_email', profileData.email);
            localStorage.setItem('gastric_buddy_recovery_code', restoreCode);
            localStorage.setItem('gb_user_id', profileData.user_id);
            localStorage.setItem('gb_email', profileData.email);
            localStorage.setItem('gb_recovery_code', restoreCode);
            
            const fullProfile = {
              ...profileData,
              recovery_code: restoreCode,
              last_saved: new Date().toISOString()
            };
            localStorage.setItem('gastric_buddy_profile', JSON.stringify(fullProfile));
            
            // Clean URL and set profile directly without reload
            window.history.replaceState({}, '', window.location.pathname);
            
            // Set profile data directly in the current session
            setUserProfile(profileData);
            
            alert(`Profile restored successfully! Welcome back, ${profileData.name}!`);
          })
          .catch(error => {
            console.error('❌ Auto-restore failed:', error);
            alert('Auto-restore failed. Please try manual recovery.');
          });
      } catch (error) {
        console.error('❌ Auto-restore error:', error);
      }
    }
    
    if (urlUserId && urlEmail) {
      console.log('🔗 Found profile in URL parameters:', urlUserId, urlEmail);
      // Persist the URL profile data
      persistProfile(urlUserId, urlEmail);
    }
    
    const finalUserId = urlUserId || savedUserId;
    const finalEmail = urlEmail || savedEmail;
    
    if (finalUserId || savedRecoveryCode) {
      console.log('✅ User session found, loading data...');
      
      // Show loading state immediately for PWA users
      if (isPWA) {
        setIsLoading(true);
      }
      
      // Add PWA-specific retry logic with session token support
      const loadWithRetry = async (retryCount = 0) => {
        try {
          // Try recovery code first (best for PWA), then fallback to user ID
          if (savedRecoveryCode && isPWA) {
            console.log('🔑 PWA detected - using recovery code authentication');
            await loadUserDataByCode(savedRecoveryCode);
          } else if (finalUserId) {
            await loadUserData(finalUserId, finalEmail);
          }
          
          // Re-persist after successful load for PWA reliability
          persistProfile(finalUserId, finalEmail, null, savedRecoveryCode);
          
          // Clean up URL parameters after successful load in PWA
          if (isPWA && (urlUserId || urlEmail)) {
            const cleanUrl = new URL(window.location);
            cleanUrl.searchParams.delete('user_id');
            cleanUrl.searchParams.delete('email');
            cleanUrl.searchParams.delete('source');
            window.history.replaceState({}, '', cleanUrl.toString());
          }
          
        } catch (error) {
          console.error(`❌ Load attempt ${retryCount + 1} failed:`, error);
          
          // If recovery code failed, try regular user ID method
          if (savedRecoveryCode && retryCount === 0) {
            console.log('🔄 Recovery code failed, trying user ID method...');
            try {
              if (finalUserId) {
                await loadUserData(finalUserId, finalEmail);
                persistProfile(finalUserId, finalEmail, null, savedRecoveryCode);
                return;
              }
            } catch (userIdError) {
              console.error('❌ User ID method also failed:', userIdError);
            }
          }
          
          // In PWA mode, be more patient with network issues
          const maxRetries = isPWA ? 8 : 3; // Increased retries for PWA
          const retryDelay = isPWA ? (retryCount + 1) * 2000 : (retryCount + 1) * 1500;
          
          if (retryCount < maxRetries) {
            console.log(`🔄 Retrying in ${retryDelay/1000} seconds... (${retryCount + 1}/${maxRetries})`);
            setTimeout(() => loadWithRetry(retryCount + 1), retryDelay);
          } else {
            console.error('❌ All retry attempts failed. User data may be temporarily unavailable.');
            setIsLoading(false);
            
            // Professional error handling for PWA
            if (isPWA) {
              const errorMsg = `We're having trouble connecting to load your profile. This usually resolves quickly.

Please try:
• Pull down to refresh the app
• Check your internet connection
• Close and reopen the app

Your profile is safe and will load once connection is restored.`;
              alert(errorMsg);
            }
          }
        }
      };
      
      loadWithRetry();
    } else {
      console.log('💁 No stored user session found - new user');
      
      // In PWA mode with no profile, suggest using browser first
      if (isPWA) {
        console.log('📱 PWA detected with no profile - may need browser setup first');
      }
    }
    // Load public data that doesn't require a user profile
    loadPublicData();
    
    // Check for payment success in URL (reuse urlParams from above)
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      handlePaymentSuccess(sessionId);
    }
  }, []);

  const loadPublicData = async () => {
    try {
      console.log('Loading public data from:', BACKEND_URL);
      
      // Load health tips
      const tipsResponse = await fetch(`${BACKEND_URL}/api/health-tips`);
      if (tipsResponse.ok) {
        const tipsData = await tipsResponse.json();
        setHealthTips(tipsData);
      } else {
        console.error('Health tips failed:', tipsResponse.status);
      }

      // Load recommended vitamins
      const vitaminsResponse = await fetch(`${BACKEND_URL}/api/recommended-vitamins`);
      if (vitaminsResponse.ok) {
        const vitaminsData = await vitaminsResponse.json();
        setRecommendedVitamins(vitaminsData);
      }

      // Load about creator
      const aboutResponse = await fetch(`${BACKEND_URL}/api/about-creator`);
      if (aboutResponse.ok) {
        const aboutData = await aboutResponse.json();
        setAboutCreator(aboutData);
      }

      // Load daily encouragement
      const encouragementResponse = await fetch(`${BACKEND_URL}/api/daily-encouragement`);
      if (encouragementResponse.ok) {
        const encouragementData = await encouragementResponse.json();
        setDailyEncouragement(encouragementData);
      }

      // Load legal documents
      const privacyResponse = await fetch(`${BACKEND_URL}/api/privacy-policy`);
      if (privacyResponse.ok) {
        const privacyData = await privacyResponse.json();
        setPrivacyPolicy(privacyData);
      }

      const termsResponse = await fetch(`${BACKEND_URL}/api/terms-of-service`);
      if (termsResponse.ok) {
        const termsData = await termsResponse.json();
        setTermsOfService(termsData);
      }

      // Load subscription packages
      const packagesResponse = await fetch(`${BACKEND_URL}/api/subscription-packages`);
      if (packagesResponse.ok) {
        const packagesData = await packagesResponse.json();
        setSubscriptionPackages(packagesData);
      }
    } catch (error) {
      console.error('Error loading public data:', error);
    }
  };

  const loadScanStatus = async (userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/daily-scan-status/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setScanStatus(data);
      }
    } catch (error) {
      console.error('Error loading scan status:', error);
    }
  };

  const validatePromoCode = async (code) => {
    if (!code.trim()) {
      setPromoDiscount(null);
      return;
    }
    
    try {
      setPromoValidating(true);
      const response = await fetch(`${BACKEND_URL}/api/validate-promo-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ promo_code: code })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setPromoDiscount(data);
      } else {
        setPromoDiscount(null);
        alert(data.message || 'Invalid promo code');
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setPromoDiscount(null);
    } finally {
      setPromoValidating(false);
    }
  };

  const handlePromoRemove = () => {
    setPromoCode('');
    setPromoDiscount(null);
  };

  // Open Stripe Customer Portal for subscription management
  const openCustomerPortal = async () => {
    try {
      console.log('🔄 Opening customer portal...');
      console.log('Premium status:', userPremiumStatus);
      
      // Create a proper email for subscription management
      const subject = encodeURIComponent('Subscription Cancellation Request - Gastric Buddy');
      const body = encodeURIComponent(`Hello,

I would like to cancel my Gastric Buddy premium subscription.

Account Details:
- Name: ${userProfile?.name || 'Not provided'}
- Email: ${userProfile?.email || 'Not provided'}
- User ID: ${userProfile?.user_id || 'Not provided'}

Please confirm the cancellation and let me know if you need any additional information.

Thank you,
${userProfile?.name || 'User'}`);

      const emailUrl = `mailto:dsc@yourgastricbuddy.com?subject=${subject}&body=${body}`;
      
      // Open email client
      window.location.href = emailUrl;
      
      // Also show instructions
      alert(`📧 EMAIL SENT TO YOUR EMAIL CLIENT

We've opened your email client with a pre-written cancellation request.

✅ WHAT TO DO:
1. Your email client should open automatically
2. The cancellation email is pre-written for you
3. Just click "Send" to submit your request
4. You'll receive confirmation within 24 hours

📧 SUPPORT EMAIL: dsc@yourgastricbuddy.com

If your email client didn't open, you can manually email us at the address above.`);
      
    } catch (error) {
      console.error('Customer portal error:', error);
      alert('Please contact support to manage your subscription.');
    }
  };

  // Comprehensive system check for debugging
  const runSystemCheck = async () => {
    console.log('=== COMPREHENSIVE SYSTEM CHECK ===');
    
    const checks = {
      backendUrl: BACKEND_URL,
      userProfile: userProfile,
      userProfileId: userProfile?.user_id,
      subscriptionPackages: subscriptionPackages,
      windowOrigin: window.location.origin,
      currentUrl: window.location.href,
      userAgent: navigator.userAgent
    };
    
    console.log('System Check Results:', checks);
    
    // Test if we can reach the backend
    try {
      console.log('Testing backend health...');
      const healthResponse = await fetch(`${BACKEND_URL}/api/health`);
      console.log('Health check status:', healthResponse.status);
      const healthData = await healthResponse.json();
      console.log('Health check data:', healthData);
      checks.backendHealth = healthData;
    } catch (healthError) {
      console.error('Backend health check failed:', healthError);
      checks.backendHealthError = healthError.message;
    }
    
    // Display comprehensive check results
    const checkResults = Object.entries(checks)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join('\n');
    
    alert(`🔍 SYSTEM CHECK RESULTS:

${checkResults}

Check browser console for detailed logs.`);
    
    return checks;
  };

  const loadPremiumContent = async (userId) => {
    try {
      // Load premium analytics
      const analyticsResponse = await fetch(`${BACKEND_URL}/api/premium-analytics/${userId}`);
      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        setPremiumAnalytics(analyticsData);
      }

      // Load premium meal plans
      const mealPlansResponse = await fetch(`${BACKEND_URL}/api/premium-meal-plans`);
      if (mealPlansResponse.ok) {
        const mealPlansData = await mealPlansResponse.json();
        setPremiumMealPlans(mealPlansData);
      }

      // Load premium educational content
      const educationResponse = await fetch(`${BACKEND_URL}/api/premium-educational-content`);
      if (educationResponse.ok) {
        const educationData = await educationResponse.json();
        setPremiumEducation(educationData);
      }
    } catch (error) {
      console.error('Error loading premium content:', error);
    }
  };

  // Load user data by recovery code (PWA-optimized)
  const loadUserDataByCode = async (recoveryCode) => {
    try {
      console.log('🔑 Loading user data by recovery code...');
      console.log('🌐 Current location:', window.location.href);
      console.log('🔗 Backend URL being used:', BACKEND_URL);
      
      setIsLoading(true);
      
      // Get profile by recovery code
      const profileResponse = await fetch(`${BACKEND_URL}/api/profile-by-code/${recoveryCode}`);
      if (!profileResponse.ok) {
        throw new Error(`Profile lookup failed: ${profileResponse.status}`);
      }
      
      const profileData = await profileResponse.json();
      console.log('✅ Profile loaded by recovery code:', profileData.name);
      
      // Add recovery code to profile data for display
      profileData.recovery_code = recoveryCode;
      setUserProfile(profileData);
      
      // Store user info for session persistence
      localStorage.setItem('gastric_buddy_user_id', profileData.user_id);
      if (profileData.email) {
        localStorage.setItem('gastric_buddy_email', profileData.email);
      }
      
      // Continue with regular data loading using the profile data
      await loadUserData(profileData.user_id, profileData.email);
      
    } catch (error) {
      console.error('❌ Recovery code authentication failed:', error);
      throw error;
    }
  };

  const loadUserData = async (userId, userEmail = null) => {
    try {
      console.log('Loading user data for:', userId, 'email:', userEmail);
      console.log('🌐 Current location:', window.location.href);  
      console.log('🔗 Backend URL being used:', BACKEND_URL);
      
      // Store user info for session persistence
      if (userEmail) {
        localStorage.setItem('gastric_buddy_email', userEmail);
      }
      localStorage.setItem('gastric_buddy_user_id', userId);
      
      setIsLoading(true);
      
      // Load profile
      const profileResponse = await fetch(`${BACKEND_URL}/api/profile/${userId}`);
      if (profileResponse.ok) {
        const profile = await profileResponse.json();
        setUserProfile(profile);
        
        // Load daily summary
        const summaryResponse = await fetch(`${BACKEND_URL}/api/daily-summary/${userId}`);
        if (summaryResponse.ok) {
          const summary = await summaryResponse.json();
          setDailySummary(summary);
        }
        
        // Load food history
        const historyResponse = await fetch(`${BACKEND_URL}/api/food-history/${userId}`);
        if (historyResponse.ok) {
          const history = await historyResponse.json();
          setFoodHistory(history.entries || []);
        }

        // Load weight history
        const weightResponse = await fetch(`${BACKEND_URL}/api/weight-history/${userId}`);
        if (weightResponse.ok) {
          const weightData = await weightResponse.json();
          setWeightHistory(weightData);
        }

        // Load vitamin history
        const vitaminResponse = await fetch(`${BACKEND_URL}/api/vitamin-history/${userId}`);
        if (vitaminResponse.ok) {
          const vitaminData = await vitaminResponse.json();
          setVitaminHistory(vitaminData.entries || []);
        }

        // Load fluid summary
        const fluidResponse = await fetch(`${BACKEND_URL}/api/fluid-summary/${userId}`);
        if (fluidResponse.ok) {
          const fluidData = await fluidResponse.json();
          setFluidSummary(fluidData);
        }

        // Load mood history
        const moodResponse = await fetch(`${BACKEND_URL}/api/mood-history/${userId}`);
        if (moodResponse.ok) {
          const moodData = await moodResponse.json();
          setMoodHistory(moodData);
        }

        // Check consent status
        const hasConsent = localStorage.getItem('gastric_buddy_consent');
        if (!hasConsent && !userProfile) {
          setShowConsentModal(true);
        } else {
          setConsentGiven(true);
        }

        // Load premium status with enhanced logging
        console.log('🔍 Loading premium status for user:', userId);
        const premiumResponse = await fetch(`${BACKEND_URL}/api/user-premium-status/${userId}`);
        if (premiumResponse.ok) {
          const premiumData = await premiumResponse.json();
          console.log('✅ Premium status loaded:', premiumData);
          setUserPremiumStatus(premiumData);
          
          // Force re-render if premium status changed
          if (premiumData.premium_active) {
            console.log('🎉 Premium is ACTIVE for user!');
          } else {
            console.log('💰 User has free tier access');
          }
        } else {
          console.error('❌ Failed to load premium status');
        }
          
        // Load scan status
        await loadScanStatus(userId);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    const maxAttempts = 3;
    
    try {
      setIsLoading(true);
      
      // Log the backend URL being used for debugging
      console.log('Backend URL being used:', BACKEND_URL);
      
      // Add retry logic for network resilience
      let attempts = 0;
      
      while (attempts < maxAttempts) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/profile`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(profileForm),
          });
          
          console.log('Profile submission response status:', response.status);
          
          if (response.ok) {
            const data = await response.json();
            console.log('✅ Profile created with recovery code:', data.recovery_code);
            
            // Save user info to localStorage for future sessions with PWA support
            persistProfile(data.user_id, profileForm.email, data, data.recovery_code);
            
            // Add recovery code to profile data for display
            const profileWithCode = { ...data, recovery_code: data.recovery_code };
            setUserProfile(profileWithCode);
            
            await loadUserData(data.user_id, profileForm.email);
            setActiveTab('home');
            alert(data.message || 'Profile created successfully! Welcome to Gastric Buddy!');
            return; // Success - exit retry loop
          } else {
            // Get detailed error message
            let errorMessage = 'Error creating profile. Please try again.';
            try {
              const errorData = await response.json();
              if (errorData.detail) {
                errorMessage = `Error: ${errorData.detail}`;
              }
            } catch (e) {
              errorMessage = `Error: Server returned ${response.status}. Please try again.`;
            }
            console.error('Profile creation failed:', response.status, errorMessage);
            
            // Don't retry on 4xx errors (user input errors)
            if (response.status >= 400 && response.status < 500) {
              alert(errorMessage);
              return;
            }
            
            throw new Error(errorMessage);
          }
        } catch (fetchError) {
          attempts++;
          console.log(`Attempt ${attempts} failed:`, fetchError.message);
          
          if (attempts < maxAttempts) {
            console.log(`Retrying in ${attempts * 2} seconds...`);
            await new Promise(resolve => setTimeout(resolve, attempts * 2000)); // Progressive delay
          } else {
            throw fetchError; // Final attempt failed
          }
        }
      }
      
    } catch (error) {
      console.error('Network error creating profile:', error);
      console.log('Failed URL:', `${BACKEND_URL}/api/profile`);
      alert(`Network error creating profile after ${maxAttempts} attempts. The server may be temporarily busy. Please wait a moment and try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
          aspectRatio: { ideal: 16/9 }
        },
        audio: false  // Explicitly disable audio to prevent video recording mode
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      
      setCamera({ active: true, stream });
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Camera access denied. Please use file upload instead.');
    }
  };

  const stopCamera = () => {
    if (camera.stream) {
      camera.stream.getTracks().forEach(track => track.stop());
    }
    setCamera({ active: false, stream: null });
    setCapturedImage(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext('2d');
      
      // Limit image size for faster processing
      const maxWidth = 1024;
      const maxHeight = 768;
      
      let { videoWidth, videoHeight } = video;
      
      // Scale down if image is too large
      if (videoWidth > maxWidth || videoHeight > maxHeight) {
        const ratio = Math.min(maxWidth / videoWidth, maxHeight / videoHeight);
        videoWidth *= ratio;
        videoHeight *= ratio;
      }
      
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      context.drawImage(video, 0, 0, videoWidth, videoHeight);
      
      // Compress image for faster upload and processing
      const imageData = canvas.toDataURL('image/jpeg', 0.7);
      setCapturedImage(imageData);
      stopCamera();
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas to compress image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Limit image size for faster processing
          const maxWidth = 1024;
          const maxHeight = 768;
          
          let { width, height } = img;
          
          // Scale down if image is too large
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress and set image
          const compressedImage = canvas.toDataURL('image/jpeg', 0.7);
          setCapturedImage(compressedImage);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeFood = async (mealType = 'snack') => {
    if (!capturedImage || !userProfile) return;
    
    try {
      setAnalyzing(true);
      
      // Show warning if it takes too long
      const warningTimeout = setTimeout(() => {
        if (analyzing) {
          console.log('Analysis taking longer than expected...');
        }
      }, 10000);
      
      const base64Data = capturedImage.split(',')[1];
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(`${BACKEND_URL}/api/analyze-food`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_base64: base64Data,
          user_id: userProfile.user_id,
          meal_type: mealType
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      clearTimeout(warningTimeout);
      
      const data = await response.json();
      
      if (response.ok) {
        // Check if this was a successful analysis
        if (data.analysis) {
          setAnalysisResult(data.analysis);
          
          // Update scan status
          if (data.scans_remaining !== undefined) {
            setScanStatus(prev => ({
              ...prev,
              scans_remaining: data.scans_remaining,
              scans_used: prev.total_limit - data.scans_remaining
            }));
          }
          
          // Refresh daily summary
          await loadUserData(userProfile.user_id);
        }
      } else {
        // Handle limit exceeded
        if (data.error === 'total_limit_reached') {
          setShowUpgradeModal(true);
          alert(`${data.message}\n\nTotal scans used: ${data.scans_used}/${data.total_limit}`);
        } else {
          alert('Error analyzing food. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error analyzing food:', error);
      if (error.name === 'AbortError') {
        alert('Analysis timed out after 15 seconds. Please try with a clearer, smaller image or check your internet connection.');
      } else if (error.message.includes('fetch')) {
        alert('Network error. Please check your internet connection and try again.');
      } else {
        alert('Error analyzing food. Please try again.');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const addWeightEntry = async () => {
    if (!newWeightEntry.weight_lbs || !userProfile) return;
    
    try {
      setIsLoading(true);
      
      const response = await fetch(`${BACKEND_URL}/api/weight-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userProfile.user_id,
          weight_lbs: parseFloat(newWeightEntry.weight_lbs),
          notes: newWeightEntry.notes
        }),
      });
      
      if (response.ok) {
        // Refresh weight history
        const weightResponse = await fetch(`${BACKEND_URL}/api/weight-history/${userProfile.user_id}`);
        if (weightResponse.ok) {
          const weightData = await weightResponse.json();
          setWeightHistory(weightData);
        }
        
        // Clear form
        setNewWeightEntry({ weight_lbs: '', notes: '' });
      } else {
        alert('Error adding weight entry. Please try again.');
      }
    } catch (error) {
      console.error('Error adding weight entry:', error);
      alert('Error adding weight entry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const addVitaminEntry = async () => {
    if (!newVitaminEntry.vitamin_name || !newVitaminEntry.dosage_mg || !userProfile) return;
    
    try {
      setIsLoading(true);
      
      const response = await fetch(`${BACKEND_URL}/api/vitamin-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userProfile.user_id,
          vitamin_name: newVitaminEntry.vitamin_name,
          dosage_mg: parseFloat(newVitaminEntry.dosage_mg),
          time_taken: newVitaminEntry.time_taken,
          notes: newVitaminEntry.notes
        }),
      });
      
      if (response.ok) {
        // Refresh vitamin history
        const vitaminResponse = await fetch(`${BACKEND_URL}/api/vitamin-history/${userProfile.user_id}`);
        if (vitaminResponse.ok) {
          const vitaminData = await vitaminResponse.json();
          setVitaminHistory(vitaminData.entries || []);
        }
        
        // Clear form
        setNewVitaminEntry({ vitamin_name: '', dosage_mg: '', time_taken: 'morning', notes: '' });
      } else {
        alert('Error adding vitamin entry. Please try again.');
      }
    } catch (error) {
      console.error('Error adding vitamin entry:', error);
      alert('Error adding vitamin entry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const addFluidEntry = async () => {
    if (!newFluidEntry.amount_oz || !userProfile) return;
    
    try {
      setIsLoading(true);
      
      const response = await fetch(`${BACKEND_URL}/api/fluid-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userProfile.user_id,
          fluid_type: newFluidEntry.fluid_type,
          amount_oz: parseFloat(newFluidEntry.amount_oz)
        }),
      });
      
      if (response.ok) {
        // Refresh fluid summary
        const fluidResponse = await fetch(`${BACKEND_URL}/api/fluid-summary/${userProfile.user_id}`);
        if (fluidResponse.ok) {
          const fluidData = await fluidResponse.json();
          setFluidSummary(fluidData);
        }
        
        // Clear form
        setNewFluidEntry({ fluid_type: 'water', amount_oz: '' });
      } else {
        alert('Error adding fluid entry. Please try again.');
      }
    } catch (error) {
      console.error('Error adding fluid entry:', error);
      alert('Error adding fluid entry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const addMoodEntry = async () => {
    if (!newMoodEntry.mood_description || !userProfile) return;
    
    try {
      setIsLoading(true);
      
      const response = await fetch(`${BACKEND_URL}/api/mood-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userProfile.user_id,
          mood_rating: parseInt(newMoodEntry.mood_rating),
          mood_description: newMoodEntry.mood_description,
          energy_level: parseInt(newMoodEntry.energy_level),
          journal_entry: newMoodEntry.journal_entry,
          gratitude_note: newMoodEntry.gratitude_note,
          challenges: newMoodEntry.challenges,
          victories: newMoodEntry.victories
        }),
      });
      
      if (response.ok) {
        // Refresh mood history
        const moodResponse = await fetch(`${BACKEND_URL}/api/mood-history/${userProfile.user_id}`);
        if (moodResponse.ok) {
          const moodData = await moodResponse.json();
          setMoodHistory(moodData);
        }
        
        // Clear form
        setNewMoodEntry({
          mood_rating: 5,
          mood_description: '',
          energy_level: 5,
          journal_entry: '',
          gratitude_note: '',
          challenges: '',
          victories: ''
        });
      } else {
        alert('Error adding mood entry. Please try again.');
      }
    } catch (error) {
      console.error('Error adding mood entry:', error);
      alert('Error adding mood entry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConsent = async (consentData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/user-consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...consentData,
          app_version: "1.0",
          timestamp: new Date().toISOString()
        }),
      });

      if (response.ok) {
        localStorage.setItem('gastric_buddy_consent', JSON.stringify(consentData));
        setConsentGiven(true);
        setShowConsentModal(false);
      }
    } catch (error) {
      console.error('Error recording consent:', error);
    }
  };

  const initiatePayment = async (packageId, promoDiscount = null) => {
    console.log('🚀 PAYMENT FUNCTION CALLED');
    console.log('Package ID:', packageId);
    console.log('User Profile:', userProfile);
    
    if (!userProfile) {
      console.error('❌ No user profile found');
      alert('Please create your profile first');
      return;
    }

    if (!userProfile.user_id) {
      console.error('❌ User profile missing user_id');
      alert('Profile error: User ID missing. Please try refreshing the page.');
      return;
    }

    try {
      console.log('✅ Starting payment process...');
      setIsLoading(true);
      
      const requestData = {
        package_id: packageId,
        origin_url: window.location.origin,
        user_id: userProfile.user_id
      };
      
      // Add promo code if present
      if (promoCode && promoDiscount) {
        requestData.promo_code = promoCode;
        console.log('✅ Adding promo code:', promoCode, 'with discount:', promoDiscount);
      }
      
      console.log('✅ Request data prepared:', requestData);
      console.log('✅ Making API call to:', `${BACKEND_URL}/api/checkout/session`);
      
      const response = await fetch(`${BACKEND_URL}/api/checkout/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      console.log('✅ Response received, status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Success! Redirecting to:', data.url);
        window.location.href = data.url;
      } else {
        console.error('❌ Response not ok:', response.status);
        const responseText = await response.text();
        console.error('❌ Error response:', responseText);
        alert(`Error creating checkout session: ${responseText}`);
      }
    } catch (error) {
      console.error('❌ CAUGHT ERROR IN PAYMENT FUNCTION:');
      console.error('Error:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      alert(`Payment Error: ${error.message}`);
    } finally {
      console.log('✅ Setting loading to false');
      setIsLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/checkout/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          // Refresh user premium status
          if (userProfile) {
            const premiumResponse = await fetch(`${BACKEND_URL}/api/user-premium-status/${userProfile.user_id}`);
            if (premiumResponse.ok) {
              const premiumData = await premiumResponse.json();
              setUserPremiumStatus(premiumData);
            }
          }
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking payment status:', error);
      return false;
    }
  };

  const handlePaymentSuccess = async (sessionId) => {
    try {
      console.log('🎉 Payment success detected, session ID:', sessionId);
      
      // Manually activate premium for this user
      if (userProfile) {
        console.log('🔄 Attempting to activate premium...');
        const activateResponse = await fetch(`${BACKEND_URL}/api/activate-premium`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userProfile.user_id,
            session_id: sessionId
          }),
        });
        
        if (activateResponse.ok) {
          const activateData = await activateResponse.json();
          console.log('✅ Premium activated successfully:', activateData);
        } else {
          const errorData = await activateResponse.text();
          console.error('❌ Premium activation failed:', errorData);
          alert('Payment received but premium activation failed. Please refresh the page or contact support.');
          return;
        }
      }
      
      // Check payment status and update user premium status
      const paymentConfirmed = await checkPaymentStatus(sessionId);
      
      if (paymentConfirmed) {
        // Show success message
        alert('🎉 Payment successful! Welcome to Gastric Buddy Premium!');
        
        // Refresh user data to get updated premium status
        if (userProfile) {
          await loadUserData(userProfile.user_id);
        }
        
        // Clear URL parameters
        const url = new URL(window.location);
        url.searchParams.delete('payment');
        url.searchParams.delete('session_id');
        window.history.replaceState({}, document.title, url.pathname);
        
        // Navigate to home tab
        setActiveTab('home');
      } else {
        console.error('Payment confirmation failed');
        alert('Payment processing... Please check your subscription status in a few minutes.');
      }
    } catch (error) {
      console.error('Error handling payment success:', error);
      alert('Payment received but there was an error updating your account. Please contact support if your premium features are not activated.');
    }
  };

  const resetCapture = () => {
    setCapturedImage(null);
    setAnalysisResult(null);
  };

  // Profile Code Display Component
  const ProfileCodeDisplay = ({ userProfile }) => {
    const [recoveryCode, setRecoveryCode] = useState(userProfile?.recovery_code || null);
    const [loading, setLoading] = useState(false);

    // Get recovery code if not already available
    const getRecoveryCode = async () => {
      if (recoveryCode || !userProfile?.user_id) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/get-recovery-code/${userProfile.user_id}`);
        if (response.ok) {
          const data = await response.json();
          setRecoveryCode(data.recovery_code);
          
          // Update the userProfile state with recovery code
          setUserProfile(prev => ({ ...prev, recovery_code: data.recovery_code }));
        }
      } catch (error) {
        console.error('Error getting recovery code:', error);
      } finally {
        setLoading(false);
      }
    };

    // Auto-fetch recovery code when component mounts
    React.useEffect(() => {
      if (!recoveryCode && userProfile?.user_id) {
        getRecoveryCode();
      }
    }, [userProfile?.user_id]);

    return (
      <div className="profile-code-display" style={{padding: '20px', background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)', margin: '15px 0', borderRadius: '12px', color: 'white', textAlign: 'center'}}>
        <h3 style={{margin: '0 0 10px 0', fontSize: '18px'}}>Your Profile Code</h3>
        {loading ? (
          <div style={{fontSize: '18px', margin: '20px 0'}}>Loading your code...</div>
        ) : recoveryCode ? (
          <>
            <div style={{fontSize: '32px', fontWeight: 'bold', fontFamily: 'monospace', letterSpacing: '4px', margin: '10px 0'}}>
              {recoveryCode}
            </div>
            <p style={{margin: '10px 0 0 0', fontSize: '14px', opacity: '0.9'}}>
              Save this code! Use it to access your profile in the mobile app.
            </p>
          </>
        ) : (
          <button 
            onClick={getRecoveryCode}
            style={{
              background: 'rgba(255,255,255,0.2)', 
              border: '1px solid rgba(255,255,255,0.3)', 
              color: 'white', 
              padding: '12px 24px', 
              borderRadius: '8px', 
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Get My Profile Code
          </button>
        )}
      </div>
    );
  };

  // Delete weight entry function
  const deleteWeightEntry = async (entryId) => {
    if (!userProfile) {
      alert('Please create your profile first');
      return;
    }

    if (!confirm('Are you sure you want to delete this weight entry? This action cannot be undone.')) {
      return;
    }

    try {
      setIsLoading(true);
      console.log('🗑️ Deleting weight entry:', entryId);

      const response = await fetch(`${BACKEND_URL}/api/weight-entry/${entryId}?user_id=${userProfile.user_id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        console.log('✅ Weight entry deleted successfully');
        
        // Refresh weight history to update progress calculations
        await loadUserData(userProfile.user_id, userProfile.email);
        
        alert('Weight entry deleted successfully! Your progress has been recalculated.');
      } else {
        const errorText = await response.text();
        console.error('❌ Delete failed:', errorText);
        alert('Failed to delete weight entry. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error deleting weight entry:', error);
      alert('Error deleting weight entry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateProgressReport = async () => {
    if (!userProfile) {
      alert('Please create your profile first');
      return;
    }

    try {
      setIsLoading(true);
      
      const response = await fetch(`${BACKEND_URL}/api/generate-progress-report/${userProfile.user_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `gastric-buddy-progress-report-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Error generating progress report. Please try again.');
      }
    } catch (error) {
      console.error('Error generating progress report:', error);
      alert('Error generating progress report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ===== Home Insights helpers (Progress Chart / Weekly Note / Streaks / Share) =====
  const isPremium = !!userPremiumStatus?.premium_active;

  const loadStreaks = async () => {
    if (!userProfile?.user_id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/streaks/${userProfile.user_id}`);
      if (res.ok) {
        const data = await res.json();
        setStreaks(data);
      }
    } catch (e) {
      // silent — streaks are non-critical
    }
  };

  const loadFreezeStatus = async () => {
    if (!userProfile?.user_id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/streaks/freeze-status/${userProfile.user_id}`);
      if (res.ok) setFreezeStatus(await res.json());
    } catch (e) { /* silent */ }
  };

  const loadMilestones = async () => {
    if (!userProfile?.user_id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/milestones/${userProfile.user_id}`);
      if (res.ok) {
        const data = await res.json();
        setMilestones(data.milestones || []);
      }
    } catch (e) { /* silent */ }
  };

  const useStreakFreeze = async () => {
    if (!userProfile?.user_id) return;
    setFreezing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/streaks/freeze/${userProfile.user_id}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        // reload both streaks and freeze status
        await Promise.all([loadStreaks(), loadFreezeStatus()]);
        alert(`❄  ${data.message}`);
      } else {
        alert(data.detail || 'Could not apply streak freeze.');
      }
    } catch (e) {
      alert('Network error. Please try again.');
    } finally {
      setFreezing(false);
    }
  };

  useEffect(() => {
    if (userProfile?.user_id && activeTab === 'home') {
      loadStreaks();
      loadFreezeStatus();
    }
    if (userProfile?.user_id && activeTab === 'about') {
      loadMilestones();
    }
  }, [userProfile?.user_id, activeTab]);

  // Weekly Digest (in-app) — show once per ISO week when user visits Home
  useEffect(() => {
    if (!userProfile?.user_id || activeTab !== 'home') return;
    if (!streaks) return; // wait for streaks + weight data
    const key = `gb_digest_week_${userProfile.user_id}`;
    const currentWeek = isoWeek();
    let lastSeen = null;
    try { lastSeen = parseInt(localStorage.getItem(key) || '0', 10); } catch (e) {}
    // Only show on Monday (day 1) — or if user missed Monday, show first visit of the week
    const today = new Date();
    const isMonday = today.getDay() === 1;
    if (lastSeen !== currentWeek && (isMonday || lastSeen === null || currentWeek - (lastSeen || 0) >= 1)) {
      // small delay so it doesn't clash with splash/streak toast
      const t = setTimeout(() => setWeeklyDigest({ open: true }), 900);
      try { localStorage.setItem(key, String(currentWeek)); } catch (e) {}
      return () => clearTimeout(t);
    }
  }, [userProfile?.user_id, activeTab, streaks]);

  // Milestone toast — fires when both streaks + premium status have loaded.
  // Runs whenever streaks OR premium status changes; detectStreakMilestones
  // writes to localStorage so the same milestone doesn't re-trigger.
  useEffect(() => {
    if (!streaks || !userProfile?.user_id) return;
    if (!userPremiumStatus?.premium_active) return;
    const events = detectStreakMilestones(userProfile.user_id, streaks);
    if (events.length) {
      setStreakToast({ show: true, events });
      // Persist to backend for the Achievements gallery
      fetch(`${BACKEND_URL}/api/milestones/${userProfile.user_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          events: events.map(e => ({ kind: e.kind, days: e.days }))
        })
      }).then(() => loadMilestones()).catch(() => {});
      const t = setTimeout(() => setStreakToast({ show: false, events: [] }), 6000);
      return () => clearTimeout(t);
    }
  }, [streaks, userPremiumStatus?.premium_active, userProfile?.user_id]);

  // Build a small SVG chart from weight entries (last N days) — delegates to <WeightTrendChart />
  const renderWeightChart = (days) => (
    <WeightTrendChart
      entries={weightHistory?.entries}
      days={days}
      isPremium={isPremium}
      goalWeight={userProfile?.goal_weight_lbs}
    />
  );

  // Build the branded share card canvas → returns {dataUrl, blob}
  const buildShareCanvas = async () => {
    if (!userProfile) return null;
    const phase = getPhaseInfo(userProfile.surgery_date);
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1350;
    const ctx = canvas.getContext('2d');
    // Background gradient
    const bg = ctx.createLinearGradient(0, 0, 0, canvas.height);
    bg.addColorStop(0, '#EAF4FF');
    bg.addColorStop(1, '#FFFFFF');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Logo
    await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const w = 520;
        const h = (img.height / img.width) * w;
        ctx.drawImage(img, (canvas.width - w) / 2, 90, w, h);
        resolve();
      };
      img.onerror = resolve;
      img.src = logo;
    });
    // Content
    ctx.fillStyle = '#0A2A5E';
    ctx.textAlign = 'center';
    ctx.font = 'bold 54px Satoshi, sans-serif';
    ctx.fillText(`Hi, I'm ${userProfile.name || 'a Gastric Buddy'}`, canvas.width / 2, 560);
    ctx.font = '600 34px Satoshi, sans-serif';
    ctx.fillStyle = '#1E90FF';
    ctx.fillText(`Phase: ${phase.label}  •  Week ${phase.weeksSince || 0}`, canvas.width / 2, 620);
    // Big stat
    const lost = weightHistory?.weight_lost_lbs || 0;
    ctx.font = 'bold 220px Satoshi, sans-serif';
    ctx.fillStyle = '#0A2A5E';
    ctx.fillText(`${lost.toFixed(1)}`, canvas.width / 2, 880);
    ctx.font = '600 44px Satoshi, sans-serif';
    ctx.fillStyle = '#505A6E';
    ctx.fillText('lbs lost so far', canvas.width / 2, 940);
    // Progress bar
    const total = (weightHistory?.weight_lost_lbs || 0) + Math.max(0, weightHistory?.remaining_to_goal_lbs || 0);
    const pct = total > 0 ? Math.min(100, ((weightHistory?.weight_lost_lbs || 0) / total) * 100) : 0;
    const barX = 140, barY = 1010, barW = canvas.width - 280, barH = 26;
    ctx.fillStyle = '#D6E7FF';
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(barX, barY, barW, barH, 13) : ctx.rect(barX, barY, barW, barH);
    ctx.fill();
    const fg = ctx.createLinearGradient(barX, 0, barX + barW, 0);
    fg.addColorStop(0, '#1E90FF');
    fg.addColorStop(1, '#0A2A5E');
    ctx.fillStyle = fg;
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(barX, barY, (barW * pct) / 100, barH, 13) : ctx.rect(barX, barY, (barW * pct) / 100, barH);
    ctx.fill();
    ctx.font = '600 30px Satoshi, sans-serif';
    ctx.fillStyle = '#0A2A5E';
    ctx.fillText(`${Math.round(pct)}% to my goal`, canvas.width / 2, 1080);
    // Weekly note
    ctx.font = 'italic 30px Satoshi, sans-serif';
    ctx.fillStyle = '#505A6E';
    const note = getWeeklyNote(phase.phaseKey);
    const words = note.split(' ');
    let line = '';
    let y = 1170;
    words.forEach((w) => {
      const test = line ? line + ' ' + w : w;
      if (ctx.measureText(test).width > canvas.width - 240) {
        ctx.fillText('"' + line + '"', canvas.width / 2, y);
        y += 42;
        line = w;
      } else {
        line = test;
      }
    });
    if (line) ctx.fillText('"' + line + '"', canvas.width / 2, y);
    // Footer
    ctx.font = '600 26px Satoshi, sans-serif';
    ctx.fillStyle = '#1E90FF';
    ctx.fillText('yourgastricbuddy.com', canvas.width / 2, 1300);

    const dataUrl = canvas.toDataURL('image/png');
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    return { dataUrl, blob };
  };

  // Free share: build → open preview modal
  const shareProgressImage = async () => {
    if (!userProfile) return;
    setSharingCard(true);
    try {
      const result = await buildShareCanvas();
      if (result) setSharePreview({ open: true, dataUrl: result.dataUrl, blob: result.blob });
    } catch (e) {
      console.error('Share image build failed', e);
      alert('Could not create share card. Please try again.');
    } finally {
      setSharingCard(false);
    }
  };

  const downloadShareImage = () => {
    if (!sharePreview.blob) return;
    const filename = `my-gastric-buddy-progress-${new Date().toISOString().slice(0, 10)}.png`;
    const url = URL.createObjectURL(sharePreview.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const nativeShareImage = async () => {
    if (!sharePreview.blob) return;
    const filename = `my-gastric-buddy-progress-${new Date().toISOString().slice(0, 10)}.png`;
    try {
      if (navigator.canShare && navigator.share) {
        const file = new File([sharePreview.blob], filename, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: 'My Gastric Buddy Progress', text: 'Sharing my post-surgery progress!' });
          return;
        }
      }
      downloadShareImage();
    } catch (e) {
      // user cancelled or share failed — fall back to download
      downloadShareImage();
    }
  };

  // Premium: multi-page PDF progress report (jsPDF)
  const downloadPremiumPDF = async () => {
    if (!userProfile) return;
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const phase = getPhaseInfo(userProfile.surgery_date);

      // Load logo as data URL
      const imgData = await new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const c = document.createElement('canvas');
          c.width = img.width;
          c.height = img.height;
          c.getContext('2d').drawImage(img, 0, 0);
          resolve(c.toDataURL('image/png'));
        };
        img.onerror = () => resolve(null);
        img.src = logo;
      });

      // Page 1 — Cover
      if (imgData) doc.addImage(imgData, 'PNG', pageW / 2 - 90, 80, 180, 118);
      doc.setTextColor(10, 42, 94);
      doc.setFontSize(26);
      doc.text('Progress Report', pageW / 2, 240, { align: 'center' });
      doc.setFontSize(14);
      doc.setTextColor(80, 90, 110);
      doc.text(`${userProfile.name || 'Gastric Buddy User'}  •  ${new Date().toLocaleDateString()}`, pageW / 2, 265, { align: 'center' });
      doc.setDrawColor(30, 144, 255);
      doc.line(80, 290, pageW - 80, 290);

      doc.setFontSize(13);
      doc.setTextColor(10, 42, 94);
      let y = 330;
      const line = (label, val) => {
        doc.setFont(undefined, 'bold');
        doc.text(label, 80, y);
        doc.setFont(undefined, 'normal');
        doc.text(String(val), 260, y);
        y += 22;
      };
      line('Phase:', `${phase.label} (Week ${phase.weeksSince || 0})`);
      line('Surgery date:', userProfile.surgery_date || 'Not set');
      line('Starting weight:', `${weightHistory?.starting_weight_lbs || '—'} lbs`);
      line('Current weight:', `${weightHistory?.current_weight_lbs || '—'} lbs`);
      line('Goal weight:', `${weightHistory?.goal_weight_lbs || '—'} lbs`);
      line('Weight lost:', `${(weightHistory?.weight_lost_lbs || 0).toFixed(1)} lbs`);
      line('Progress to goal:', `${weightHistory?.progress_percentage || 0}%`);

      // Page 2 — Streaks & consistency
      doc.addPage();
      doc.setFontSize(20);
      doc.setTextColor(10, 42, 94);
      doc.text('Consistency & Streaks', 80, 90);
      doc.setDrawColor(30, 144, 255);
      doc.line(80, 100, pageW - 80, 100);
      y = 140;
      doc.setFontSize(13);
      line('Vitamin streak:', `${streaks?.vitamin_streak_days || 0} days (best: ${streaks?.vitamin_best_streak_days || 0})`);
      line('Fluid streak:', `${streaks?.fluid_streak_days || 0} days (best: ${streaks?.fluid_best_streak_days || 0})`);
      line('Vitamin days (last 30):', `${streaks?.vitamin_days_last_30 || 0} / 30`);
      line('Fluid days (last 30):', `${streaks?.fluid_days_last_30 || 0} / 30`);
      y += 20;
      doc.setFont(undefined, 'italic');
      doc.setTextColor(80, 90, 110);
      doc.setFontSize(11);
      const tip = PREMIUM_TIPS[phase.phaseKey] || '';
      const wrapped = doc.splitTextToSize(`Denise's tip for this phase: ${tip}`, pageW - 160);
      doc.text(wrapped, 80, y);

      // Page 3 — Weight log
      doc.addPage();
      doc.setFont(undefined, 'normal');
      doc.setFontSize(20);
      doc.setTextColor(10, 42, 94);
      doc.text('Weight Log', 80, 90);
      doc.line(80, 100, pageW - 80, 100);
      doc.setFontSize(11);
      doc.setTextColor(10, 42, 94);
      y = 130;
      doc.text('Date', 90, y);
      doc.text('Weight (lbs)', 240, y);
      doc.text('Notes', 380, y);
      y += 14;
      doc.setDrawColor(220, 220, 220);
      doc.line(80, y, pageW - 80, y);
      y += 12;
      const entries = (weightHistory?.entries || []).slice(0, 30);
      doc.setTextColor(60, 60, 60);
      entries.forEach((e) => {
        if (y > 780) { doc.addPage(); y = 90; }
        doc.text(String(e.date || ''), 90, y);
        doc.text(String(e.weight_lbs || ''), 240, y);
        doc.text(String((e.notes || '').slice(0, 40)), 380, y);
        y += 16;
      });

      // Footer
      const pages = doc.getNumberOfPages();
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(140, 150, 170);
        doc.text('Gastric Buddy — Track. Plan. Progress.  •  yourgastricbuddy.com', pageW / 2, 820, { align: 'center' });
      }

      doc.save(`gastric-buddy-report-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error('PDF failed', e);
      alert('Could not build PDF right now. Please try again.');
    }
  };

  // Reusable "profile required" gate — shown when free/anonymous users click a tracking tab
  const renderProfileRequired = (featureName, reason) => (
    <div className="tab-content">
      <div className="profile-required-card" data-testid="profile-required-card">
        <div className="profile-required-icon">
          <img src={logo} alt="Gastric Buddy" />
        </div>
        <h2 className="heading-3">Create your profile to use {featureName}</h2>
        <p className="profile-required-reason">{reason}</p>
        <ul className="profile-required-list">
          <li><Check size={14} strokeWidth={2.5} /> Takes under a minute</li>
          <li><Check size={14} strokeWidth={2.5} /> Everything you log is tied to your recovery journey</li>
          <li><Check size={14} strokeWidth={2.5} /> Your data stays on your device + our secure database</li>
        </ul>
        <button
          className="btn-primary profile-required-cta"
          onClick={() => setActiveTab('profile')}
          data-testid="profile-required-create-btn"
        >
          Create Your Profile
        </button>
        <button
          className="profile-required-back"
          onClick={() => setActiveTab('home')}
          data-testid="profile-required-back-btn"
        >
          ← Back to Home
        </button>
      </div>
    </div>
  );

  // Render functions for different tabs
  const renderHome = () => {
    if (!userProfile) {
      return (
        <div className="tab-content">
          <div className="welcome-section">
            <img src={logo} alt="Gastric Buddy" className="app-logo-lg" />
            <h2 className="heading-2">Welcome to Gastric Buddy</h2>
            <p className="body-large">Your companion for post-gastric sleeve nutrition tracking</p>
            <button 
              className="btn-primary"
              onClick={() => setActiveTab('profile')}
              data-testid="create-profile-btn"
            >
              Create Your Profile
            </button>
          </div>

          {/* Free vs Premium teaser — visible BEFORE profile creation */}
          <div className="tier-teaser" data-testid="tier-teaser">
            <div className="tier-teaser-header">
              <span className="tier-teaser-eyebrow">What's included</span>
              <h3 className="tier-teaser-title">Start free. Go further with Premium.</h3>
            </div>
            <div className="tier-teaser-grid">
              <div className="tier-col tier-free" data-testid="tier-free">
                <div className="tier-col-header">
                  <Heart size={16} strokeWidth={2.2} />
                  <span>Free</span>
                </div>
                <ul className="tier-list">
                  <li><Check size={14} strokeWidth={2.5} /> Daily food, vitamin & fluid logging</li>
                  <li><Check size={14} strokeWidth={2.5} /> 3 AI food scans</li>
                  <li><Check size={14} strokeWidth={2.5} /> Weight tracking with 7-day chart</li>
                  <li><Check size={14} strokeWidth={2.5} /> Mood journal & daily tips</li>
                  <li><Check size={14} strokeWidth={2.5} /> Weekly note from Denise</li>
                  <li><Check size={14} strokeWidth={2.5} /> Share your progress as an image</li>
                </ul>
              </div>
              <div className="tier-col tier-premium" data-testid="tier-premium">
                <div className="tier-col-header premium-header">
                  <Gem size={16} strokeWidth={2.2} />
                  <span>Premium</span>
                  <span className="tier-col-tag">Everything in Free, plus</span>
                </div>
                <ul className="tier-list">
                  <li><Sparkles size={14} strokeWidth={2.5} /> <strong>Unlimited</strong> AI food scans</li>
                  <li><Sparkles size={14} strokeWidth={2.5} /> Full 7-day meal plans by surgery phase</li>
                  <li><Sparkles size={14} strokeWidth={2.5} /> Supplement timing & exercise progression</li>
                  <li><Sparkles size={14} strokeWidth={2.5} /> 30/60/90-day trends + goal projection</li>
                  <li><Sparkles size={14} strokeWidth={2.5} /> Vitamin & fluid streaks + streak freeze</li>
                  <li><Sparkles size={14} strokeWidth={2.5} /> Multi-page PDF progress reports</li>
                  <li><Sparkles size={14} strokeWidth={2.5} /> Personal check-in with Denise</li>
                </ul>
                <button
                  className="btn-primary tier-preview-btn"
                  onClick={() => setActiveTab('profile')}
                  data-testid="tier-teaser-cta"
                >
                  <Gem size={14} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Create profile & explore Premium
                </button>
                <p className="tier-teaser-footnote">Start free — upgrade anytime.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="tab-content">
        <div className="user-greeting">
          <h2 className="heading-3">Hello, {userProfile.name}!</h2>
          <p className="body-medium">Here's your daily progress</p>
          
          {/* Update Profile Button */}
          <button 
            className="btn-secondary"
            onClick={() => {
              // Pre-populate form with existing data
              if (userProfile) {
                setProfileForm({
                  name: userProfile.name || '',
                  email: userProfile.email || '',
                  age: userProfile.age?.toString() || '',
                  gender: userProfile.gender || 'female',
                  height_feet: userProfile.height_feet?.toString() || '',
                  height_inches: userProfile.height_inches?.toString() || '',
                  current_weight_lbs: userProfile.current_weight_lbs?.toString() || '',
                  goal_weight_lbs: userProfile.goal_weight_lbs?.toString() || '',
                  activity_level: userProfile.activity_level || 'light',
                  surgery_date: userProfile.surgery_date || ''
                });
              }
              setActiveTab('profile');
            }}
            style={{ marginTop: '10px', fontSize: '14px' }}
          >
            ✏️ Update Profile
          </button>
        </div>

        {/* Slim Premium teaser — only for FREE users on Home */}
        {!isPremium && (
          <div className="premium-teaser-slim" data-testid="premium-teaser-slim">
            <div className="premium-teaser-icon">
              <Gem size={18} strokeWidth={2.2} />
            </div>
            <div className="premium-teaser-body">
              <div className="premium-teaser-title">Unlock the full Gastric Buddy</div>
              <div className="premium-teaser-desc">Unlimited AI food scans • 7-day meal plans • streaks • PDF reports • check-ins with Denise</div>
            </div>
            <button
              className="premium-teaser-cta"
              onClick={() => setShowUpgradeModal(true)}
              data-testid="premium-teaser-cta"
            >
              See Premium
            </button>
          </div>
        )}

        {/* Daily Encouragement */}
        {dailyEncouragement && (
          <div className="daily-encouragement-home">
            <div className="encouragement-card-mini">
              <div className="encouragement-message-mini">
                "{dailyEncouragement.daily_message.message}"
              </div>
              <div className="encouragement-author-mini">
                - {dailyEncouragement.daily_message.author}
              </div>
            </div>
          </div>
        )}

        {/* ===== Home Insights (Progress • Weekly Note • Streaks • Share) ===== */}
        {(() => {
          const phase = getPhaseInfo(userProfile.surgery_date);
          const note = getWeeklyNote(phase.phaseKey);
          const premiumTip = PREMIUM_TIPS[phase.phaseKey] || '';
          const windows = isPremium ? [30, 60, 90] : [7];
          return (
            <div className="home-insights">
              {/* Weekly Note */}
              <div className="insight-card weekly-note-card" data-testid="weekly-note-card">
                <div className="insight-header">
                  <Heart size={16} strokeWidth={2.2} className="insight-icon" />
                  <span className="insight-title">This Week's Note</span>
                  <span className="insight-phase-chip">{phase.label}</span>
                </div>
                <p className="weekly-note-text">"{note}"</p>
                <div className="weekly-note-author">— Denise St. Charles</div>
                {isPremium ? (
                  <div className="premium-tip-block" data-testid="premium-weekly-tip">
                    <div className="premium-tip-label">
                      <Gem size={12} strokeWidth={2.5} /> Premium tip
                    </div>
                    <p className="premium-tip-text">{premiumTip}</p>
                    <button
                      className="btn-secondary premium-tip-cta"
                      onClick={() => setActiveTab('premium')}
                      data-testid="book-checkin-btn"
                    >
                      Book a personal check-in with Denise →
                    </button>
                  </div>
                ) : (
                  <button
                    className="insight-upsell"
                    onClick={() => setShowUpgradeModal(true)}
                    data-testid="weekly-note-upsell"
                  >
                    <Gem size={12} strokeWidth={2.5} /> Unlock a deeper phase-specific tip + a check-in with Denise
                  </button>
                )}
              </div>

              {/* Progress Chart */}
              <div className="insight-card chart-card" data-testid="progress-chart-card">
                <div className="insight-header">
                  <BarChart3 size={16} strokeWidth={2.2} className="insight-icon" />
                  <span className="insight-title">Weight Trend</span>
                  <div className="chart-window-tabs">
                    {windows.map((w) => (
                      <button
                        key={w}
                        className={`chart-window-tab ${chartWindow === w ? 'active' : ''}`}
                        onClick={() => setChartWindow(w)}
                        data-testid={`chart-window-${w}`}
                      >
                        {w}d
                      </button>
                    ))}
                  </div>
                </div>
                {renderWeightChart(isPremium ? chartWindow : 7)}
                {!isPremium && (
                  <button
                    className="insight-upsell"
                    onClick={() => setShowUpgradeModal(true)}
                    data-testid="chart-upsell"
                  >
                    <Gem size={12} strokeWidth={2.5} /> Unlock 30/60/90-day trends, moving average & goal projection
                  </button>
                )}
              </div>

              {/* Streaks */}
              <div className="insight-card streaks-card" data-testid="streaks-card">
                <div className="insight-header">
                  <Award size={16} strokeWidth={2.2} className="insight-icon" />
                  <span className="insight-title">Streaks</span>
                  {!isPremium && <span className="premium-badge"><Gem size={11} strokeWidth={2.5} /> Premium</span>}
                </div>
                {isPremium ? (
                  <>
                    <div className="streaks-grid">
                      <div className="streak-stat">
                        <Pill size={18} strokeWidth={2.2} />
                        <div className="streak-num" data-testid="vitamin-streak">{streaks?.vitamin_streak_days ?? 0}</div>
                        <div className="streak-label">Vitamin streak</div>
                        <div className="streak-sub">Best {streaks?.vitamin_best_streak_days ?? 0} • {streaks?.vitamin_days_last_30 ?? 0}/30 days</div>
                      </div>
                      <div className="streak-stat">
                        <Droplet size={18} strokeWidth={2.2} />
                        <div className="streak-num" data-testid="fluid-streak">{streaks?.fluid_streak_days ?? 0}</div>
                        <div className="streak-label">Fluid streak</div>
                        <div className="streak-sub">Best {streaks?.fluid_best_streak_days ?? 0} • {streaks?.fluid_days_last_30 ?? 0}/30 days</div>
                      </div>
                    </div>
                    {/* Streak Freeze — one per calendar month */}
                    <div className="streak-freeze-row" data-testid="streak-freeze-row">
                      {freezeStatus?.freeze_available_this_month ? (
                        <button
                          className="streak-freeze-btn"
                          onClick={useStreakFreeze}
                          disabled={freezing}
                          data-testid="use-freeze-btn"
                        >
                          <Snowflake size={14} strokeWidth={2.2} />
                          {freezing ? 'Applying...' : 'Use my streak freeze (1 per month)'}
                        </button>
                      ) : (
                        <div className="streak-freeze-used" data-testid="freeze-used">
                          <Snowflake size={14} strokeWidth={2.2} />
                          Freeze used {freezeStatus?.used_freeze_date ? `on ${freezeStatus.used_freeze_date}` : 'this month'} — resets next month
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="streaks-locked">
                    <p className="streaks-locked-text">Track vitamin & fluid streaks (3, 7, 30-day milestones) to keep momentum going every day.</p>
                    <button
                      className="btn-primary"
                      onClick={() => setShowUpgradeModal(true)}
                      data-testid="streaks-upsell"
                    >
                      <Gem size={14} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Unlock streaks
                    </button>
                  </div>
                )}
              </div>

              {/* Share */}
              <div className="insight-card share-card" data-testid="share-card">
                <div className="insight-header">
                  <FileText size={16} strokeWidth={2.2} className="insight-icon" />
                  <span className="insight-title">Share your progress</span>
                </div>
                <p className="share-desc">Send your care team (or yourself) a beautiful snapshot of how far you've come.</p>
                <div className="share-actions">
                  <button
                    className="btn-secondary share-btn"
                    onClick={shareProgressImage}
                    disabled={sharingCard}
                    data-testid="share-image-btn"
                  >
                    {sharingCard ? 'Preparing...' : (<><Camera size={15} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Share Image</>)}
                  </button>
                  {isPremium ? (
                    <button
                      className="btn-primary share-btn"
                      onClick={downloadPremiumPDF}
                      data-testid="download-pdf-btn"
                    >
                      <FileText size={15} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Download PDF
                    </button>
                  ) : (
                    <button
                      className="insight-upsell inline-upsell"
                      onClick={() => setShowUpgradeModal(true)}
                      data-testid="pdf-upsell"
                    >
                      <Gem size={12} strokeWidth={2.5} /> Full multi-page PDF report is Premium
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Premium Analytics Section */}
        {userPremiumStatus?.premium_active && (
          <div className="premium-analytics">
            <h3 className="section-title">
              <span className="premium-badge"><Gem size={13} strokeWidth={2.5} /> Premium</span> 
              Advanced Analytics
              <button 
                className="btn-secondary manage-subscription-btn"
                onClick={() => openCustomerPortal()}
                style={{ marginLeft: '10px', fontSize: '12px' }}
              >
                ⚙️ Manage Subscription
              </button>
            </h3>
            
            {premiumAnalytics && (
              <>
                <div className="analytics-grid">
                  <div className="analytics-card">
                    <h4>Weight Trends</h4>
                    <div className="trend-metrics">
                      <div className="metric">
                        <span className="metric-label">Weekly Average:</span>
                        <span className="metric-value">{premiumAnalytics.weight_trends.weekly_average}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Goal Timeline:</span>
                        <span className="metric-value">{premiumAnalytics.weight_trends.goal_timeline}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Success Probability:</span>
                        <span className="metric-value success-rate">{premiumAnalytics.weight_trends.success_probability}</span>
                      </div>
                    </div>
                  </div>

                  <div className="analytics-card">
                    <h4>Nutrition Insights</h4>
                    <div className="nutrition-scores">
                      <div className="score-item">
                        <span>Protein Consistency</span>
                        <div className="score-bar">
                          <div className="score-fill" style={{width: premiumAnalytics.nutrition_insights.protein_consistency}}></div>
                          <span className="score-text">{premiumAnalytics.nutrition_insights.protein_consistency}</span>
                        </div>
                      </div>
                      <div className="score-item">
                        <span>Hydration Compliance</span>
                        <div className="score-bar">
                          <div className="score-fill" style={{width: premiumAnalytics.nutrition_insights.hydration_compliance}}></div>
                          <span className="score-text">{premiumAnalytics.nutrition_insights.hydration_compliance}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <button 
                  className="btn-primary view-full-analytics"
                  onClick={() => setShowPremiumContent(true)}
                >
                  View Full Analytics Report
                </button>
              </>
            )}
          </div>
        )}

        {/* REMOVED: Premium activation helper for security - users must pay through Stripe */}
        
        {/* PWA Profile Code Display */}
        {userProfile && !window.matchMedia('(display-mode: standalone)').matches && (
          <ProfileCodeDisplay userProfile={userProfile} />
        )}
        
        {/* Professional PWA Installation Helper */}
        {userProfile && !window.matchMedia('(display-mode: standalone)').matches && (
          <div className="pwa-install-prompt" style={{padding: '20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', margin: '15px 0', borderRadius: '12px', color: 'white', textAlign: 'center'}}>
            <h3 style={{margin: '0 0 10px 0', fontSize: '18px'}}>Install Mobile App</h3>
            <p style={{margin: '0 0 15px 0', fontSize: '14px', opacity: '0.9'}}>
              Add to your home screen for the best mobile experience
            </p>
            <button 
              onClick={() => {
                // Hide this prompt
                document.querySelector('.pwa-install-prompt').style.display = 'none';
                
                // Show native install prompt if available
                if (window.deferredPrompt) {
                  window.deferredPrompt.prompt();
                } else {
                  alert('To install: Tap your browser menu and select "Add to Home Screen"');
                }
              }}
              style={{
                background: 'rgba(255,255,255,0.2)', 
                border: '1px solid rgba(255,255,255,0.3)', 
                color: 'white', 
                padding: '12px 24px', 
                borderRadius: '8px', 
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Install App
            </button>
          </div>
        )}

        {/* Premium Upgrade Prompt for Free Users */}
        {userPremiumStatus && !userPremiumStatus.premium_active && (
          <div className="premium-preview">
            <h3 className="section-title">Unlock Premium Analytics</h3>
            <div className="premium-preview-card">
              <div className="premium-features">
                <div className="feature-preview"><BarChart3 size={15} strokeWidth={2.2} className="fp-icon" /> Advanced weight trend analysis</div>
                <div className="feature-preview"><Target size={15} strokeWidth={2.2} className="fp-icon" /> Personalized goal timeline predictions</div>
                <div className="feature-preview"><TrendingUp size={15} strokeWidth={2.2} className="fp-icon" /> Nutrition compliance scoring</div>
                <div className="feature-preview"><Lightbulb size={15} strokeWidth={2.2} className="fp-icon" /> Behavioral pattern insights</div>
                <div className="feature-preview"><FileText size={15} strokeWidth={2.2} className="fp-icon" /> Detailed progress reports (PDF)</div>
                <div className="feature-preview"><Pill size={15} strokeWidth={2.2} className="fp-icon" /> Custom meal plans by phase</div>
                <div className="feature-preview"><GraduationCap size={15} strokeWidth={2.2} className="fp-icon" /> Exclusive educational content</div>
              </div>
              <button 
                className="btn-primary premium-upgrade-btn"
                onClick={() => setShowUpgradeModal(true)}
              >
                <Gem size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Upgrade to Premium
              </button>
            </div>
          </div>
        )}

        {dailySummary && (
          <div className="daily-progress">
            <div className="progress-card">
              <h3 className="heading-4">Today's Nutrition</h3>
              
              <div className="macro-grid">
                <div className="macro-item">
                  <div className="macro-label">Calories</div>
                  <div className="macro-progress">
                    <div className="macro-bar">
                      <div 
                        className="macro-fill calories"
                        style={{
                          width: `${Math.min((dailySummary.consumed.calories / dailySummary.goals.calories_goal) * 100, 100)}%`
                        }}
                      />
                    </div>
                    <div className="macro-text">
                      {dailySummary.consumed.calories} / {dailySummary.goals.calories_goal}
                    </div>
                  </div>
                </div>

                <div className="macro-item">
                  <div className="macro-label">Protein</div>
                  <div className="macro-progress">
                    <div className="macro-bar">
                      <div 
                        className="macro-fill protein"
                        style={{
                          width: `${Math.min((dailySummary.consumed.protein / dailySummary.goals.protein_goal) * 100, 100)}%`
                        }}
                      />
                    </div>
                    <div className="macro-text">
                      {dailySummary.consumed.protein}g / {dailySummary.goals.protein_goal}g
                    </div>
                  </div>
                </div>

                <div className="macro-item">
                  <div className="macro-label">Carbs</div>
                  <div className="macro-progress">
                    <div className="macro-bar">
                      <div 
                        className="macro-fill carbs"
                        style={{
                          width: `${Math.min((dailySummary.consumed.carbs / dailySummary.goals.carbs_goal) * 100, 100)}%`
                        }}
                      />
                    </div>
                    <div className="macro-text">
                      {dailySummary.consumed.carbs}g / {dailySummary.goals.carbs_goal}g
                    </div>
                  </div>
                </div>

                <div className="macro-item">
                  <div className="macro-label">Fat</div>
                  <div className="macro-progress">
                    <div className="macro-bar">
                      <div 
                        className="macro-fill fat"
                        style={{
                          width: `${Math.min((dailySummary.consumed.fat / dailySummary.goals.fat_goal) * 100, 100)}%`
                        }}
                      />
                    </div>
                    <div className="macro-text">
                      {dailySummary.consumed.fat}g / {dailySummary.goals.fat_goal}g
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {weightHistory && weightHistory.current_weight_lbs && (
          <div className="weight-progress">
            <div className="progress-card">
              <h3 className="heading-4">Weight Progress</h3>
              
              <div className="weight-stats">
                <div className="weight-stat">
                  <div className="weight-label">Current</div>
                  <div className="weight-value">{weightHistory.current_weight_lbs} lbs</div>
                </div>
                <div className="weight-stat">
                  <div className="weight-label">Goal</div>
                  <div className="weight-value">{weightHistory.goal_weight_lbs} lbs</div>
                </div>
                <div className="weight-stat">
                  <div className="weight-label">Lost</div>
                  <div className="weight-value highlight">{weightHistory.weight_lost_lbs.toFixed(1)} lbs</div>
                </div>
                <div className="weight-stat">
                  <div className="weight-label">To Goal</div>
                  <div className="weight-value">{Math.max(0, weightHistory.remaining_to_goal_lbs).toFixed(1)} lbs</div>
                </div>
              </div>
              
              <div className="weight-progress-bar">
                <div className="weight-bar">
                  <div 
                    className="weight-fill"
                    style={{
                      width: `${Math.min((weightHistory.weight_lost_lbs / (weightHistory.weight_lost_lbs + Math.max(0, weightHistory.remaining_to_goal_lbs))) * 100, 100)}%`
                    }}
                  />
                </div>
                <div className="weight-percentage">
                  {Math.round((weightHistory.weight_lost_lbs / (weightHistory.weight_lost_lbs + Math.max(0, weightHistory.remaining_to_goal_lbs))) * 100)}% to goal
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="quick-actions">
          <button 
            className="btn-primary"
            onClick={() => setActiveTab('scan')}
          >
            📸 Scan Food
          </button>
          
          {userPremiumStatus && !userPremiumStatus.premium_active && (
            <button 
              className="btn-secondary upgrade-btn"
              onClick={() => setShowUpgradeModal(true)}
            >
              <Gem size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Upgrade to Premium
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderScan = () => {
    if (!userProfile) return renderProfileRequired('Food Scanner', 'AI food scanning is tied to your daily log — set up your profile first so we can save your scans and count nutrients toward your goals.');
    return (
      <div className="tab-content">
        <div className="scan-header">
          <h2 className="heading-3">Scan Your Food</h2>
          <p className="body-medium">Take a photo or upload an image to analyze nutrition</p>
          
          {/* Scan Counter */}
          {userProfile && (
            <div className="scan-counter">
              {scanStatus.is_premium ? (
                <div className="premium-badge">
                  <span className="premium-icon">⭐</span>
                  <span className="premium-text">Premium - Unlimited Scans</span>
                </div>
              ) : (
                <div className="scan-limits">
                  <span className="scans-remaining">
                    {scanStatus.scans_remaining} of {scanStatus.total_limit} free scans remaining
                  </span>
                  {scanStatus.scans_remaining === 0 && (
                    <button 
                      className="btn-primary upgrade-btn-small"
                      onClick={() => setShowUpgradeModal(true)}
                    >
                      <Gem size={14} strokeWidth={2.2} style={{ verticalAlign: '-2px', marginRight: 5 }} /> Upgrade for Unlimited
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {!capturedImage && !camera.active && (
          <div className="scan-options">
            <button 
              className="btn-primary"
              onClick={() => cameraInputRef.current?.click()}
            >
              📸 Take Photo
            </button>
            <button 
              className="btn-secondary"
              onClick={() => fileInputRef.current?.click()}
            >
              📂 Upload Image
            </button>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {camera.active && (
          <div className="camera-container">
            <video
              ref={videoRef}
              className="camera-video"
              autoPlay
              playsInline
            />
            <div className="camera-controls">
              <button className="btn-primary" onClick={capturePhoto}>
                📸 Capture
              </button>
              <button className="btn-secondary" onClick={stopCamera}>
                ❌ Cancel
              </button>
            </div>
          </div>
        )}

        {capturedImage && !analysisResult && (
          <div className="image-preview">
            <img src={capturedImage} alt="Captured food" className="preview-image" />
            <div className="meal-type-selector">
              <label className="body-medium">Meal Type:</label>
              <div className="meal-buttons">
                <button 
                  className="btn-secondary"
                  onClick={() => analyzeFood('breakfast')}
                  disabled={analyzing}
                >
                  🌅 Breakfast
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => analyzeFood('lunch')}
                  disabled={analyzing}
                >
                  🌞 Lunch
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => analyzeFood('dinner')}
                  disabled={analyzing}
                >
                  🌙 Dinner
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => analyzeFood('snack')}
                  disabled={analyzing}
                >
                  🍎 Snack
                </button>
              </div>
            </div>
            <div className="preview-controls">
              <button className="btn-secondary" onClick={resetCapture}>
                🔄 Retake
              </button>
            </div>
            {analyzing && (
              <div className="analyzing-indicator">
                <div className="spinner"></div>
                <p className="body-medium">🔍 AI analyzing your food...</p>
                <p className="analyzing-subtitle">This usually takes 5-10 seconds</p>
                <div className="analyzing-tips">
                  <p className="tip-text"><Lightbulb size={14} strokeWidth={2.2} style={{ verticalAlign: '-2px', marginRight: 5, color: 'var(--brand-primary)' }} />Tip: Clear, well-lit photos work best!</p>
                </div>
              </div>
            )}
          </div>
        )}

        {analysisResult && (
          <div className="analysis-results">
            <div className="analysis-card">
              <h3 className="heading-4">Food Analysis</h3>
              
              <div className="food-items">
                <strong>Detected Food:</strong>
                <p className="body-medium">{analysisResult.food_items.join(', ')}</p>
              </div>

              <div className="nutrition-grid">
                <div className="nutrition-item">
                  <span className="nutrition-label">Calories</span>
                  <span className="nutrition-value">{analysisResult.total_calories}</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-label">Protein</span>
                  <span className="nutrition-value">{analysisResult.protein_g}g</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-label">Carbs</span>
                  <span className="nutrition-value">{analysisResult.carbs_g}g</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-label">Fat</span>
                  <span className="nutrition-value">{analysisResult.fat_g}g</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-label">Fiber</span>
                  <span className="nutrition-value">{analysisResult.fiber_g}g</span>
                </div>
                <div className="nutrition-item">
                  <span className="nutrition-label">Sugar</span>
                  <span className="nutrition-value">{analysisResult.sugar_g}g</span>
                </div>
              </div>

              <div className="gastric-rating">
                <div className={`rating-badge rating-${analysisResult.gastric_sleeve_rating}`}>
                  {analysisResult.gastric_sleeve_rating.toUpperCase()} for Gastric Sleeve
                </div>
              </div>

              <div className="recommendations">
                <h4 className="heading-4">Recommendations</h4>
                <p className="body-medium">{analysisResult.recommendations}</p>
              </div>

              <div className="meal-timing">
                <h4 className="heading-4">Meal Timing</h4>
                <p className="body-medium">{analysisResult.meal_timing_note}</p>
              </div>

              <div className="analysis-actions">
                <button 
                  className="btn-primary"
                  onClick={() => {
                    resetCapture();
                    setActiveTab('home');
                  }}
                >
                  ✅ Done
                </button>
                <button className="btn-secondary" onClick={resetCapture}>
                  📸 Scan Another
                </button>
              </div>
            </div>
          </div>
        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    );
  };

  const renderProfile = () => {
    return (
      <div className="tab-content">
        {/* Profile Recovery for PWA */}
        {!userProfile && window.matchMedia('(display-mode: standalone)').matches && (
          <div className="profile-recovery" style={{margin: '20px 0', padding: '20px', background: '#f0f8ff', borderRadius: '12px', border: '2px solid #2196F3'}}>
            <h3 style={{margin: '0 0 15px 0', color: '#1976D2'}}>Restore Your Profile</h3>
            <p style={{margin: '0 0 15px 0', fontSize: '14px'}}>
              Enter your 6-digit profile code from the website to restore your data:
            </p>
            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
              <input
                type="text"
                placeholder="123456"
                maxLength="6"
                style={{
                  padding: '12px',
                  fontSize: '18px',
                  fontFamily: 'monospace',
                  letterSpacing: '2px',
                  textAlign: 'center',
                  borderRadius: '8px',
                  border: '2px solid #ddd',
                  width: '120px'
                }}
                onChange={(e) => {
                  const code = e.target.value.replace(/\D/g, ''); // Only digits
                  e.target.value = code;
                }}
                onKeyPress={async (e) => {
                  if (e.key === 'Enter' && e.target.value.length === 6) {
                    try {
                      setIsLoading(true);
                      console.log('🔍 PWA Recovery Debug (Enter key):');
                      console.log('- Recovery code:', e.target.value);
                      console.log('- Backend URL:', BACKEND_URL);
                      console.log('- Full URL:', `${BACKEND_URL}/api/profile-by-code/${e.target.value}`);
                      
                      const response = await fetch(`${BACKEND_URL}/api/profile-by-code/${e.target.value}`);
                      console.log('- Response status:', response.status);
                      console.log('- Response ok:', response.ok);
                      
                      if (response.ok) {
                        const profileData = await response.json();
                        setUserProfile(profileData);
                        persistProfile(profileData.user_id, profileData.email, profileData, null, e.target.value);
                        await loadUserData(profileData.user_id, profileData.email);
                        setActiveTab('home');
                        alert('Profile restored successfully!');
                      } else {
                        alert('Invalid or expired profile code. Please check the code and try again.');
                      }
                    } catch (error) {
                      console.error('🚨 PWA Recovery Error:', error);
                      console.error('- Error type:', error.name);
                      console.error('- Error message:', error.message);
                      alert(`Error restoring profile: ${error.message}. Please check console for details.`);
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
              />
              <button
                onClick={async (e) => {
                  const input = e.target.previousElementSibling;
                  if (input.value.length === 6) {
                    try {
                      setIsLoading(true);
                      console.log('🔍 PWA Recovery Debug:');
                      console.log('- Recovery code:', input.value);
                      console.log('- Backend URL:', BACKEND_URL);
                      console.log('- Full URL:', `${BACKEND_URL}/api/profile-by-code/${input.value}`);
                      
                      const response = await fetch(`${BACKEND_URL}/api/profile-by-code/${input.value}`);
                      console.log('- Response status:', response.status);
                      console.log('- Response ok:', response.ok);
                      
                      if (response.ok) {
                        const profileData = await response.json();
                        setUserProfile(profileData);
                        persistProfile(profileData.user_id, profileData.email, profileData, null, input.value);
                        await loadUserData(profileData.user_id, profileData.email);
                        setActiveTab('home');
                        alert('Profile restored successfully!');
                      } else {
                        alert('Invalid or expired profile code. Please check the code and try again.');
                      }
                    } catch (error) {
                      console.error('🚨 PWA Recovery Error:', error);
                      console.error('- Error type:', error.name);
                      console.error('- Error message:', error.message);
                      alert(`Error restoring profile: ${error.message}. Please check console for details.`);
                    } finally {
                      setIsLoading(false);
                    }
                  } else {
                    alert('Please enter a 6-digit profile code');
                  }
                }}
                disabled={isLoading}
                style={{
                  padding: '12px 20px',
                  background: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                {isLoading ? 'Restoring...' : 'Restore'}
              </button>
            </div>
            <div style={{margin: '15px 0 0 0', padding: '10px', background: '#fff3cd', borderRadius: '6px', fontSize: '12px'}}>
              💡 Don't have a code? Visit yourgastricbuddy.com in your browser to create your profile first.
            </div>
          </div>
        )}
        
        <div className="profile-header">
          <h2 className="heading-3">
            {userProfile ? 'Update Your Profile' : 'Create Your Profile'}
          </h2>
          <p className="body-medium">{userProfile ? 'Update your information to keep your data safe' : 'Help us personalize your gastric sleeve journey'}</p>
          
          {/* Email Missing Alert for Existing Users */}
          {userProfile && !userProfile.email && (
            <div className="email-alert" style={{
              background: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: '#856404', margin: '0 0 8px 0' }}>📧 Add Your Email Address</h4>
              <p style={{ color: '#856404', margin: '0', fontSize: '14px' }}>
                Adding your email will keep your profile safe across devices and prevent data loss.
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleProfileSubmit} className="profile-form">
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              type="text"
              className="form-input"
              value={profileForm.name}
              onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
              placeholder={userProfile?.name || 'Enter your full name'}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              className="form-input"
              value={profileForm.email}
              onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
              placeholder={userProfile?.email || 'your.email@example.com'}
              required
            />
            <small className="form-help">
              {userProfile && !userProfile.email ? 
                '⚠️ Add your email to prevent losing your data when switching devices' :
                'Used to keep your progress safe and for account recovery'
              }
            </small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Age</label>
              <input
                type="number"
                className="form-input"
                value={profileForm.age}
                onChange={(e) => setProfileForm({...profileForm, age: e.target.value})}
                required
                min="18"
                max="100"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Gender</label>
              <select
                className="form-select"
                value={profileForm.gender}
                onChange={(e) => setProfileForm({...profileForm, gender: e.target.value})}
                required
              >
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Height (feet)</label>
              <select
                className="form-select"
                value={profileForm.height_feet}
                onChange={(e) => setProfileForm({...profileForm, height_feet: e.target.value})}
                required
              >
                <option value="">Select feet</option>
                <option value="4">4 ft</option>
                <option value="5">5 ft</option>
                <option value="6">6 ft</option>
                <option value="7">7 ft</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Height (inches)</label>
              <select
                className="form-select"
                value={profileForm.height_inches}
                onChange={(e) => setProfileForm({...profileForm, height_inches: e.target.value})}
                required
              >
                <option value="">Select inches</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i} value={i}>{i} in</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Current Weight (lbs)</label>
              <input
                type="number"
                className="form-input"
                value={profileForm.current_weight_lbs}
                onChange={(e) => setProfileForm({...profileForm, current_weight_lbs: e.target.value})}
                required
                min="80"
                max="600"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Goal Weight (lbs)</label>
              <input
                type="number"
                className="form-input"
                value={profileForm.goal_weight_lbs}
                onChange={(e) => setProfileForm({...profileForm, goal_weight_lbs: e.target.value})}
                required
                min="80"
                max="400"
                step="0.1"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Activity Level</label>
            <select
              className="form-select"
              value={profileForm.activity_level}
              onChange={(e) => setProfileForm({...profileForm, activity_level: e.target.value})}
              required
            >
              <option value="sedentary">Sedentary (desk job, no exercise)</option>
              <option value="light">Light (light exercise 1-3 days/week)</option>
              <option value="moderate">Moderate (moderate exercise 3-5 days/week)</option>
              <option value="active">Active (hard exercise 6-7 days/week)</option>
              <option value="very_active">Very Active (physical job + exercise)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Surgery Date (Optional)</label>
            <input
              type="date"
              className="form-input"
              value={profileForm.surgery_date}
              onChange={(e) => setProfileForm({...profileForm, surgery_date: e.target.value})}
            />
          </div>

          {/* Consent Section */}
          <div className="consent-section">
            <h4 className="heading-4">Data Use Consent</h4>
            <p className="consent-intro">
              To provide you with personalized health insights, we need your consent to collect and process your health data.
            </p>
            
            <div className="consent-checkboxes">
              <label className="consent-checkbox">
                <input type="checkbox" required />
                <span className="checkmark"></span>
                I have read and agree to the <button type="button" className="link-button" onClick={() => setActiveTab('privacy')}>Privacy Policy</button>
              </label>
              
              <label className="consent-checkbox">
                <input type="checkbox" required />
                <span className="checkmark"></span>
                I have read and agree to the <button type="button" className="link-button" onClick={() => setActiveTab('terms')}>Terms of Service</button>
              </label>
              
              <label className="consent-checkbox">
                <input type="checkbox" required />
                <span className="checkmark"></span>
                I consent to the collection and processing of my health data (weight, nutrition, mood) for app functionality
              </label>
              
              <label className="consent-checkbox">
                <input type="checkbox" required />
                <span className="checkmark"></span>
                I consent to sending food images to AI services for nutritional analysis (images are not stored)
              </label>
            </div>
          </div>

          <button 
            type="submit" 
            className="btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 
              (userProfile ? 'Updating Profile...' : 'Creating Profile...') : 
              (userProfile ? 'Update Profile' : 'Complete Setup')
            }
          </button>
        </form>
      </div>
    );
  };

  const renderVitamins = () => {
    if (!userProfile) return renderProfileRequired('Vitamin Tracker', 'Log your bariatric vitamins on the correct schedule for your surgery phase — set up your profile first.');
    return (
      <div className="tab-content">
        <div className="vitamins-header">
          <h2 className="heading-3">Vitamin Tracker</h2>
          <p className="body-medium">Track your daily vitamins and supplements</p>
        </div>

        {/* Add new vitamin entry */}
        <div className="vitamin-entry-form">
          <div className="progress-card">
            <h3 className="heading-4">Log Vitamin/Supplement</h3>
            
            <div className="form-group">
              <label className="form-label">Vitamin Name</label>
              <select
                className="form-select"
                value={newVitaminEntry.vitamin_name}
                onChange={(e) => setNewVitaminEntry({...newVitaminEntry, vitamin_name: e.target.value})}
              >
                <option value="">Select vitamin</option>
                <option value="Multivitamin">Multivitamin</option>
                <option value="Calcium Citrate">Calcium Citrate</option>
                <option value="Vitamin B12">Vitamin B12</option>
                <option value="Iron">Iron</option>
                <option value="Vitamin D3">Vitamin D3</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Dosage (mg)</label>
                <input
                  type="number"
                  className="form-input"
                  value={newVitaminEntry.dosage_mg}
                  onChange={(e) => setNewVitaminEntry({...newVitaminEntry, dosage_mg: e.target.value})}
                  placeholder="Enter dosage"
                  step="0.1"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Time Taken</label>
                <select
                  className="form-select"
                  value={newVitaminEntry.time_taken}
                  onChange={(e) => setNewVitaminEntry({...newVitaminEntry, time_taken: e.target.value})}
                >
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                  <option value="evening">Evening</option>
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <input
                type="text"
                className="form-input"
                value={newVitaminEntry.notes}
                onChange={(e) => setNewVitaminEntry({...newVitaminEntry, notes: e.target.value})}
                placeholder="Any additional notes"
              />
            </div>
            
            <button 
              className="btn-primary"
              onClick={addVitaminEntry}
              disabled={isLoading || !newVitaminEntry.vitamin_name || !newVitaminEntry.dosage_mg}
            >
              {isLoading ? 'Adding...' : (<><Pill size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Log Vitamin</>)}
            </button>
          </div>
        </div>

        {/* Recommended vitamins */}
        {recommendedVitamins && (
          <div className="recommended-vitamins">
            <div className="progress-card">
              <h3 className="heading-4">Recommended Daily Vitamins</h3>
              
              <div className="vitamin-recommendations">
                {recommendedVitamins.daily_vitamins.map((vitamin, index) => (
                  <div key={index} className="vitamin-recommendation">
                    <div className="vitamin-name">{vitamin.name}</div>
                    <div className="vitamin-dosage">{vitamin.dosage}</div>
                    <div className="vitamin-timing">Take in {vitamin.timing}</div>
                    <div className="vitamin-notes">{vitamin.notes}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Vitamin history */}
        {vitaminHistory.length > 0 ? (
          <div className="vitamin-history">
            <div className="progress-card">
              <h3 className="heading-4">Recent Vitamin Entries</h3>
              
              <div className="vitamin-entries-list">
                {vitaminHistory.slice(0, 10).map((entry, index) => (
                  <div key={entry.entry_id || index} className="vitamin-entry-item">
                    <div className="vitamin-entry-name">{entry.vitamin_name}</div>
                    <div className="vitamin-entry-details">
                      <span>{entry.dosage_mg}mg</span>
                      <span>{entry.time_taken}</span>
                      <span>{new Date(entry.taken_date).toLocaleDateString()}</span>
                    </div>
                    {entry.notes && (
                      <div className="vitamin-entry-notes">{entry.notes}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <p className="body-large">No vitamin entries yet</p>
            <p className="body-medium">Start tracking your vitamins!</p>
          </div>
        )}
      </div>
    );
  };

  const renderFluids = () => {
    if (!userProfile) return renderProfileRequired('Fluid Tracker', 'Hydration goals depend on your phase and weight — set up your profile so we can tailor your daily target.');
    return (
      <div className="tab-content">
        <div className="fluids-header">
          <h2 className="heading-3">Fluid Tracker</h2>
          <p className="body-medium">Track your daily fluid intake</p>
        </div>

        {/* Add new fluid entry */}
        <div className="fluid-entry-form">
          <div className="progress-card">
            <h3 className="heading-4">Log Fluid Intake</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Fluid Type</label>
                <select
                  className="form-select"
                  value={newFluidEntry.fluid_type}
                  onChange={(e) => setNewFluidEntry({...newFluidEntry, fluid_type: e.target.value})}
                >
                  <option value="water">Water</option>
                  <option value="tea">Tea</option>
                  <option value="coffee">Coffee</option>
                  <option value="juice">Juice</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Amount (oz)</label>
                <input
                  type="number"
                  className="form-input"
                  value={newFluidEntry.amount_oz}
                  onChange={(e) => setNewFluidEntry({...newFluidEntry, amount_oz: e.target.value})}
                  placeholder="Enter amount"
                  step="0.1"
                  min="1"
                  max="32"
                />
              </div>
            </div>
            
            <button 
              className="btn-primary"
              onClick={addFluidEntry}
              disabled={isLoading || !newFluidEntry.amount_oz}
            >
              {isLoading ? 'Adding...' : (<><Droplet size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Log Fluid</>)}
            </button>
          </div>
        </div>

        {/* Daily fluid summary */}
        {fluidSummary && (
          <div className="fluid-summary">
            <div className="progress-card">
              <h3 className="heading-4">Today's Fluid Intake</h3>
              
              <div className="fluid-progress">
                <div className="fluid-total">
                  <span className="fluid-amount">{fluidSummary.total_fluid_oz} oz</span>
                  <span className="fluid-goal">/ {fluidSummary.fluid_goal_oz} oz goal</span>
                </div>
                
                <div className="fluid-progress-bar">
                  <div 
                    className="fluid-progress-fill"
                    style={{
                      width: `${Math.min((fluidSummary.total_fluid_oz / fluidSummary.fluid_goal_oz) * 100, 100)}%`
                    }}
                  />
                </div>
                
                <div className="fluid-remaining">
                  {fluidSummary.remaining_oz > 0 ? 
                    `${fluidSummary.remaining_oz} oz to go` : 
                    'Goal reached! 🎉'
                  }
                </div>
              </div>

              {Object.keys(fluidSummary.fluid_by_type).length > 0 && (
                <div className="fluid-breakdown">
                  <h4 className="heading-4">Breakdown by Type</h4>
                  {Object.entries(fluidSummary.fluid_by_type).map(([type, amount]) => (
                    <div key={type} className="fluid-type-item">
                      <span className="fluid-type-name">{type}</span>
                      <span className="fluid-type-amount">{amount} oz</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTips = () => {
    if (!healthTips) return <div>Loading tips...</div>;

    return (
      <div className="tab-content">
        <div className="tips-header">
          <h2 className="heading-3">Health Tips & Guidelines</h2>
          <p className="body-medium">Essential guidance for your gastric sleeve journey</p>
        </div>

        {/* Do's and Don'ts */}
        <div className="dos-donts-section">
          <div className="progress-card">
            <h3 className="heading-4">Do's and Don'ts</h3>
            
            <div className="dos-donts-grid">
              <div className="dos-section">
                <h4 className="dos-title"><Check size={16} strokeWidth={2.5} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--success)' }} /> DO</h4>
                <ul className="dos-list">
                  {healthTips.dos.map((item, index) => (
                    <li key={index} className="dos-item">{item}</li>
                  ))}
                </ul>
              </div>
              
              <div className="donts-section">
                <h4 className="donts-title"><X size={16} strokeWidth={2.5} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--error)' }} /> DON'T</h4>
                <ul className="donts-list">
                  {healthTips.donts.map((item, index) => (
                    <li key={index} className="donts-item">{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Food Swaps */}
        <div className="food-swaps-section">
          <div className="progress-card">
            <h3 className="heading-4">Healthy Food Swaps</h3>
            
            <div className="food-swaps-list">
              {healthTips.food_swaps.map((swap, index) => (
                <div key={index} className="food-swap-item">
                  <div className="swap-instead">
                    <span className="swap-label">Instead of:</span>
                    <span className="swap-food">{swap.instead_of}</span>
                  </div>
                  <div className="swap-arrow">→</div>
                  <div className="swap-choose">
                    <span className="swap-label">Choose:</span>
                    <span className="swap-food highlight">{swap.choose}</span>
                  </div>
                  <div className="swap-reason">{swap.reason}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fluid Tips */}
        <div className="fluid-tips-section">
          <div className="progress-card">
            <h3 className="heading-4"><Droplet size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--brand-primary)' }} /> Fluid Intake Tips</h3>
            
            <ul className="tips-list">
              {healthTips.fluid_tips.map((tip, index) => (
                <li key={index} className="tip-item">{tip}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Vitamin Reminders */}
        <div className="vitamin-tips-section">
          <div className="progress-card">
            <h3 className="heading-4"><Pill size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--brand-primary)' }} /> Vitamin Reminders</h3>
            
            <ul className="tips-list">
              {healthTips.vitamin_reminders.map((reminder, index) => (
                <li key={index} className="tip-item">{reminder}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Exercise Tips with Pictures */}
        {healthTips.exercise_tips && (
          <div className="exercise-tips-section">
            <div className="progress-card">
              <h3 className="heading-4">🏃‍♀️ Exercise Guidelines by Phase</h3>
              
              <div className="exercise-phases">
                {healthTips.exercise_tips.map((phase, index) => (
                  <div key={index} className="exercise-phase">
                    <div className="phase-header">
                      <h4 className="phase-title">{phase.phase}</h4>
                      <div className="phase-duration">{phase.duration}</div>
                    </div>
                    
                    <img 
                      src={phase.image} 
                      alt={`Exercise for ${phase.phase}`}
                      className="exercise-image"
                    />
                    
                    <div className="phase-activities">
                      <strong>Activities:</strong> {phase.activities.join(', ')}
                    </div>
                    
                    <div className="phase-notes">
                      <em>{phase.notes}</em>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Side Effects & Management */}
        {healthTips.side_effects && (
          <div className="side-effects-section">
            <div className="progress-card">
              <h3 className="heading-4"><AlertTriangle size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--warning)' }} /> Common Side Effects & Management</h3>
              
              <div className="side-effects-list">
                {healthTips.side_effects.map((effect, index) => (
                  <div key={index} className="side-effect-item">
                    <div className="side-effect-header">
                      <h4 className="side-effect-name">{effect.symptom}</h4>
                      <div className="side-effect-description">{effect.description}</div>
                    </div>
                    
                    <div className="side-effect-management">
                      <strong>Management:</strong>
                      <ul className="management-list">
                        {effect.management.map((tip, idx) => (
                          <li key={idx} className="management-item">{tip}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="call-doctor">
                      <strong>📞 Call your doctor if:</strong> {effect.when_to_call_doctor}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderWeight = () => {
    if (!userProfile) return renderProfileRequired('Weight Tracker', 'Weight tracking starts with a starting weight and a goal — set up your profile so we can chart your progress.');
    return (
      <div className="tab-content">
        <div className="weight-header">
          <h2 className="heading-3">Weight Tracker</h2>
          <p className="body-medium">Track your progress toward your goal weight</p>
        </div>

        {/* Add new weight entry */}
        <div className="weight-entry-form">
          <div className="progress-card">
            <h3 className="heading-4">Log Your Weight</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Weight (lbs)</label>
                <input
                  type="number"
                  className="form-input"
                  value={newWeightEntry.weight_lbs}
                  onChange={(e) => setNewWeightEntry({...newWeightEntry, weight_lbs: e.target.value})}
                  placeholder="Enter weight"
                  step="0.1"
                  min="80"
                  max="600"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  value={newWeightEntry.notes}
                  onChange={(e) => setNewWeightEntry({...newWeightEntry, notes: e.target.value})}
                  placeholder="How are you feeling?"
                />
              </div>
            </div>
            
            <button 
              className="btn-primary"
              onClick={addWeightEntry}
              disabled={isLoading || !newWeightEntry.weight_lbs}
            >
              {isLoading ? 'Adding...' : (<><Scale size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Log Weight</>)}
            </button>
          </div>
        </div>

        {/* Weight progress summary */}
        {weightHistory && weightHistory.current_weight_lbs && (
          <div className="weight-summary">
            <div className="progress-card">
              <h3 className="heading-4">Your Progress</h3>
              
              <div className="weight-stats-grid">
                <div className="weight-stat-card">
                  <div className="weight-stat-value">{weightHistory.current_weight_lbs} lbs</div>
                  <div className="weight-stat-label">Current Weight</div>
                </div>
                
                <div className="weight-stat-card">
                  <div className="weight-stat-value">{weightHistory.goal_weight_lbs} lbs</div>
                  <div className="weight-stat-label">Goal Weight</div>
                </div>
                
                <div className="weight-stat-card success">
                  <div className="weight-stat-value">-{weightHistory.weight_lost_lbs.toFixed(1)} lbs</div>
                  <div className="weight-stat-label">Weight Lost</div>
                </div>
                
                <div className="weight-stat-card">
                  <div className="weight-stat-value">{Math.max(0, weightHistory.remaining_to_goal_lbs).toFixed(1)} lbs</div>
                  <div className="weight-stat-label">To Goal</div>
                </div>
              </div>
              
              <div className="weight-progress-visual">
                <div className="weight-progress-bar">
                  <div 
                    className="weight-progress-fill"
                    style={{
                      width: `${Math.min((weightHistory.weight_lost_lbs / (weightHistory.weight_lost_lbs + Math.max(0, weightHistory.remaining_to_goal_lbs))) * 100, 100)}%`
                    }}
                  />
                </div>
                <div className="weight-progress-text">
                  {Math.round((weightHistory.weight_lost_lbs / (weightHistory.weight_lost_lbs + Math.max(0, weightHistory.remaining_to_goal_lbs))) * 100)}% to goal
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Weight history */}
        {weightHistory && weightHistory.entries && weightHistory.entries.length > 0 ? (
          <div className="weight-history">
            <div className="progress-card">
              <h3 className="heading-4">Recent Entries</h3>
              
              <div className="weight-entries-list">
                {weightHistory.entries.slice(0, 10).map((entry, index) => (
                  <div key={entry.entry_id || index} className="weight-entry-item">
                    <div className="weight-entry-main">
                      <div className="weight-entry-date">
                        {new Date(entry.date).toLocaleDateString()}
                      </div>
                      <div className="weight-entry-weight">
                        {entry.weight_lbs} lbs
                      </div>
                      {entry.notes && (
                        <div className="weight-entry-notes">
                          {entry.notes}
                        </div>
                      )}
                    </div>
                    
                    {/* Only show delete button for entries that are not initial entries */}
                    {entry.notes !== "Initial weight entry" && entry.entry_id && (
                      <div className="weight-entry-actions">
                        <button
                          className="btn-delete"
                          onClick={() => deleteWeightEntry(entry.entry_id)}
                          disabled={isLoading}
                          title="Delete this weight entry"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <p className="body-large">No weight entries yet</p>
            <p className="body-medium">Start tracking your weight to see your progress!</p>
          </div>
        )}
      </div>
    );
  };

  const renderHistory = () => {
    return (
      <div className="tab-content">
        <div className="history-header">
          <h2 className="heading-3">Food History</h2>
          <p className="body-medium">Your recent food entries</p>
        </div>

        {foodHistory.length === 0 ? (
          <div className="empty-state">
            <p className="body-large">No food entries yet</p>
            <button 
              className="btn-primary"
              onClick={() => setActiveTab('scan')}
            >
              📸 Scan Your First Food
            </button>
            
            <button 
              className="btn-secondary"
              onClick={runSystemCheck}
              style={{ marginTop: '10px', fontSize: '12px', backgroundColor: '#f0f0f0', color: '#666' }}
            >
              🔍 System Check (Troubleshooting)
            </button>
          </div>
        ) : (
          <div className="history-list">
            {foodHistory.map((entry, index) => (
              <div key={entry.entry_id || index} className="history-item">
                <div className="history-time">
                  {new Date(entry.timestamp).toLocaleDateString()} - {entry.meal_type}
                </div>
                <div className="history-food">
                  {entry.food_analysis.food_items.join(', ')}
                </div>
                <div className="history-nutrition">
                  <span>{entry.food_analysis.total_calories} cal</span>
                  <span>{entry.food_analysis.protein_g}g protein</span>
                  <span className={`rating-badge rating-${entry.food_analysis.gastric_sleeve_rating}`}>
                    {entry.food_analysis.gastric_sleeve_rating}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMood = () => {
    if (!userProfile) return renderProfileRequired('Mood & Journal', 'Your mood entries are saved to your journal — set up your profile so nothing you write gets lost.');
    return (
      <div className="tab-content">
        <div className="mood-header">
          <h2 className="heading-3">Mood & Journal</h2>
          <p className="body-medium">Track your emotional journey and mental wellness</p>
        </div>

        {/* Daily Encouragement */}
        {dailyEncouragement && (
          <div className="daily-encouragement">
            <div className="encouragement-card">
              <div className="encouragement-icon"><Heart size={28} strokeWidth={2.2} fill="currentColor" style={{ color: 'var(--brand-primary)' }} /></div>
              <div className="encouragement-message">
                "{dailyEncouragement.daily_message.message}"
              </div>
              <div className="encouragement-author">
                - {dailyEncouragement.daily_message.author}
              </div>
              <div className="creator-note">
                <em>{dailyEncouragement.creator_note}</em>
              </div>
            </div>
          </div>
        )}

        {/* Mood Entry Form */}
        <div className="mood-entry-form">
          <div className="progress-card">
            <h3 className="heading-4">How are you feeling today?</h3>
            
            {/* Mood Rating */}
            <div className="form-group">
              <label className="form-label">Overall Mood (1-10)</label>
              <div className="mood-slider-container">
                <input
                  type="range"
                  min="1"
                  max="10"
                  className="mood-slider"
                  value={newMoodEntry.mood_rating}
                  onChange={(e) => setNewMoodEntry({...newMoodEntry, mood_rating: e.target.value})}
                />
                <div className="mood-value">{newMoodEntry.mood_rating}/10</div>
              </div>
            </div>

            {/* Mood Description */}
            <div className="form-group">
              <label className="form-label">Mood Description</label>
              <select
                className="form-select"
                value={newMoodEntry.mood_description}
                onChange={(e) => setNewMoodEntry({...newMoodEntry, mood_description: e.target.value})}
              >
                <option value="">Select how you're feeling</option>
                <option value="happy">Happy 😊</option>
                <option value="content">Content 😌</option>
                <option value="hopeful">Hopeful 🌟</option>
                <option value="confident">Confident 💪</option>
                <option value="grateful">Grateful 🙏</option>
                <option value="neutral">Neutral 😐</option>
                <option value="tired">Tired 😴</option>
                <option value="stressed">Stressed 😰</option>
                <option value="anxious">Anxious 😟</option>
                <option value="frustrated">Frustrated 😤</option>
                <option value="sad">Sad 😢</option>
                <option value="overwhelmed">Overwhelmed 😵</option>
              </select>
            </div>

            {/* Energy Level */}
            <div className="form-group">
              <label className="form-label">Energy Level (1-10)</label>
              <div className="mood-slider-container">
                <input
                  type="range"
                  min="1"
                  max="10"
                  className="energy-slider"
                  value={newMoodEntry.energy_level}
                  onChange={(e) => setNewMoodEntry({...newMoodEntry, energy_level: e.target.value})}
                />
                <div className="energy-value">{newMoodEntry.energy_level}/10</div>
              </div>
            </div>

            {/* Journal Entry */}
            <div className="form-group">
              <label className="form-label">Journal Entry (Optional)</label>
              <textarea
                className="form-textarea"
                rows="4"
                placeholder="How was your day? What's on your mind?"
                value={newMoodEntry.journal_entry}
                onChange={(e) => setNewMoodEntry({...newMoodEntry, journal_entry: e.target.value})}
              />
            </div>

            {/* Gratitude Note */}
            <div className="form-group">
              <label className="form-label">What are you grateful for today?</label>
              <textarea
                className="form-textarea"
                rows="2"
                placeholder="Even small things count..."
                value={newMoodEntry.gratitude_note}
                onChange={(e) => setNewMoodEntry({...newMoodEntry, gratitude_note: e.target.value})}
              />
            </div>

            {/* Challenges & Victories */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Today's Challenges</label>
                <textarea
                  className="form-textarea"
                  rows="2"
                  placeholder="What was difficult today?"
                  value={newMoodEntry.challenges}
                  onChange={(e) => setNewMoodEntry({...newMoodEntry, challenges: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Today's Victories</label>
                <textarea
                  className="form-textarea"
                  rows="2"
                  placeholder="What went well today?"
                  value={newMoodEntry.victories}
                  onChange={(e) => setNewMoodEntry({...newMoodEntry, victories: e.target.value})}
                />
              </div>
            </div>
            
            <button 
              className="btn-primary"
              onClick={addMoodEntry}
              disabled={isLoading || !newMoodEntry.mood_description}
            >
              {isLoading ? 'Saving...' : (<><Heart size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Save Entry</>)}
            </button>
          </div>
        </div>

        {/* Mood Trends */}
        {moodHistory && moodHistory.total_entries > 0 && (
          <div className="mood-trends">
            <div className="progress-card">
              <h3 className="heading-4">Your Mood Trends</h3>
              
              <div className="trend-stats">
                <div className="trend-stat">
                  <div className="trend-label">Average Mood</div>
                  <div className="trend-value mood-trend">{moodHistory.average_mood}/10</div>
                </div>
                <div className="trend-stat">
                  <div className="trend-label">Average Energy</div>
                  <div className="trend-value energy-trend">{moodHistory.average_energy}/10</div>
                </div>
                <div className="trend-stat">
                  <div className="trend-label">Entries</div>
                  <div className="trend-value">{moodHistory.total_entries}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Mood Entries */}
        {moodHistory && moodHistory.entries && moodHistory.entries.length > 0 ? (
          <div className="mood-history">
            <div className="progress-card">
              <h3 className="heading-4">Recent Journal Entries</h3>
              
              <div className="mood-entries-list">
                {moodHistory.entries.slice(0, 5).map((entry, index) => (
                  <div key={entry.entry_id || index} className="mood-entry-item">
                    <div className="mood-entry-header">
                      <div className="mood-entry-date">
                        {new Date(entry.date).toLocaleDateString()}
                      </div>
                      <div className="mood-entry-ratings">
                        <span className="mood-rating">Mood: {entry.mood_rating}/10</span>
                        <span className="energy-rating">Energy: {entry.energy_level}/10</span>
                      </div>
                    </div>
                    
                    <div className="mood-entry-description">
                      <strong>{entry.mood_description}</strong>
                    </div>
                    
                    {entry.journal_entry && (
                      <div className="mood-entry-journal">
                        <em>"{entry.journal_entry}"</em>
                      </div>
                    )}
                    
                    {entry.gratitude_note && (
                      <div className="mood-entry-gratitude">
                        <strong>Grateful for:</strong> {entry.gratitude_note}
                      </div>
                    )}
                    
                    {(entry.victories || entry.challenges) && (
                      <div className="mood-entry-vc">
                        {entry.victories && (
                          <div className="victory">
                            <strong>Victory:</strong> {entry.victories}
                          </div>
                        )}
                        {entry.challenges && (
                          <div className="challenge">
                            <strong>Challenge:</strong> {entry.challenges}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <p className="body-large">No mood entries yet</p>
            <p className="body-medium">Start tracking your emotional wellness journey!</p>
          </div>
        )}
      </div>
    );
  };

  const renderAbout = () => {
    return (
      <div className="tab-content">
        <div className="about-header">
          <h2 className="heading-3">Meet the Creator</h2>
          <p className="body-medium">Learn about the person behind Gastric Buddy</p>
        </div>

        {aboutCreator && (
          <div className="about-content">
            <div className="creator-intro">
              <h3 className="creator-name">{aboutCreator.name}</h3>
              <p className="creator-title">{aboutCreator.title}</p>
              <p className="creator-experience">{aboutCreator.experience}</p>
            </div>

            <div className="creator-story">
              <h4 className="section-title">My Story</h4>
              <div className="story-content">
                <p className="story-text">{aboutCreator.story}</p>
              </div>
            </div>

            <div className="creator-credentials">
              <h4 className="section-title">Credentials & Experience</h4>
              <div className="credentials-list">
                {aboutCreator.credentials?.map((credential, index) => (
                  <div key={index} className="credential-item">
                    <span className="credential-icon"><Award size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', color: 'var(--brand-navy)' }} /></span>
                    <span className="credential-text">{credential}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="published-books">
              <h4 className="section-title">Published Books</h4>
              <div className="books-grid">
                {aboutCreator.books?.map((book, index) => (
                  <div key={index} className="book-item" data-testid={`book-item-${index}`}>
                    <div className="book-info">
                      <h5 className="book-title">{book.title}</h5>
                      <p className="book-description">{book.description}</p>
                      {book.year && <span className="book-year">Published: {book.year}</span>}
                      {book.amazon_url && (
                        <a
                          href={book.amazon_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="book-amazon-btn"
                          data-testid={`book-amazon-${index}`}
                        >
                          <ShoppingBag size={14} strokeWidth={2.2} /> Buy on Amazon
                          <ExternalLink size={11} strokeWidth={2.2} className="ext-icon" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Achievements gallery */}
            <div className="achievements-section" data-testid="achievements-section">
              <h4 className="section-title">
                <Trophy size={16} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6, color: '#FFB020' }} />
                Your Achievements
              </h4>
              {milestones.length === 0 ? (
                <div className="achievements-empty" data-testid="achievements-empty">
                  <Award size={26} strokeWidth={1.6} />
                  <p>Keep logging vitamins & fluids daily — your first streak milestone unlocks at 3 days.</p>
                </div>
              ) : (
                <div className="achievements-grid">
                  {milestones.map((m, i) => (
                    <div key={i} className={`achievement-badge kind-${m.kind}`} data-testid={`achievement-${m.kind}-${m.days}`}>
                      <div className="achievement-badge-icon">
                        {m.kind === 'vitamin' ? <Pill size={20} strokeWidth={2.2} /> : <Droplet size={20} strokeWidth={2.2} />}
                      </div>
                      <div className="achievement-badge-days">{m.days}</div>
                      <div className="achievement-badge-label">
                        Day {m.kind === 'vitamin' ? 'Vitamin' : 'Fluid'} Streak
                      </div>
                      <div className="achievement-badge-date">
                        Earned {new Date(m.earned_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="books-availability">
              <a 
                href="https://www.amazon.com/stores/Denise-St.-Charles/author/B0FNVZD697?ref=ap_rdr&isDramIntegrated=true&shoppingPortalEnabled=true&ccs_id=post-op-tracker"
                target="_blank"
                rel="noopener noreferrer"
                className="amazon-link"
              >
                <strong>{aboutCreator.books_availability}</strong>
              </a>
            </div>

            <div className="creator-mission">
              <h4 className="section-title">My Mission</h4>
              <p className="mission-text">{aboutCreator.mission}</p>
            </div>

            <div className="contact-info">
              <h4 className="section-title">Get in Touch</h4>
              <div className="contact-details">
                <div className="contact-item">
                  <span className="contact-icon">📧</span>
                  <a 
                    href={`mailto:${aboutCreator.contact_email}?subject=Hello from Gastric Buddy User`}
                    className="contact-email-link"
                    style={{
                      color: '#007cba',
                      textDecoration: 'none',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {aboutCreator.contact_email}
                  </a>
                </div>
                
                <div className="contact-buttons" style={{ marginTop: '15px' }}>
                  <button 
                    className="btn-primary"
                    onClick={() => {
                      const subject = encodeURIComponent('Support Request - Gastric Buddy');
                      const body = encodeURIComponent(`Hello Denise,

I need help with my Gastric Buddy account.

My Details:
- Name: ${userProfile?.name || 'Not provided'}
- Email: ${userProfile?.email || 'Not provided'}

My Question/Issue:
[Please describe your question or issue here]

Thank you for your help!`);

                      window.location.href = `mailto:${aboutCreator.contact_email}?subject=${subject}&body=${body}`;
                    }}
                    style={{ marginRight: '10px' }}
                  >
                    📧 Contact Support
                  </button>
                  
                  <button 
                    className="btn-secondary"
                    onClick={() => {
                      const subject = encodeURIComponent('Subscription Help - Gastric Buddy');
                      const body = encodeURIComponent(`Hello,

I need help with my Gastric Buddy subscription.

Account Details:
- Name: ${userProfile?.name || 'Not provided'}
- Email: ${userProfile?.email || 'Not provided'}
- User ID: ${userProfile?.user_id || 'Not provided'}

Request Type:
[ ] Cancel subscription
[ ] Change subscription plan
[ ] Billing question
[ ] Other: ___________

Please let me know how to proceed.

Thank you!`);

                      window.location.href = `mailto:dsc@yourgastricbuddy.com?subject=${subject}&body=${body}`;
                    }}
                  >
                    💳 Subscription Help
                  </button>
                </div>
                {aboutCreator.social_media && (
                  <div className="social-links">
                    {aboutCreator.social_media.map((social, index) => (
                      <a key={index} href={social.url} className="social-link" target="_blank" rel="noopener noreferrer">
                        <span className="social-icon">{social.icon}</span>
                        <span className="social-name">{social.platform}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!aboutCreator && (
          <div className="loading-state">
            <p>Loading creator information...</p>
          </div>
        )}
      </div>
    );
  };

  // Premium feature helper functions
  const loadPremiumAnalytics = async () => {
    if (!userProfile) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/premium-analytics/${userProfile.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setPremiumData(prev => ({ ...prev, analytics: data }));
      }
    } catch (error) {
      console.error('Error loading premium analytics:', error);
    }
  };

  const loadPremiumMealPlans = async () => {
    if (!userProfile) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/premium-meal-plans/${userProfile.user_id}`);
      if (response.ok) {
        const data = await response.json();
        setPremiumData(prev => ({ ...prev, mealPlans: data }));
      }
    } catch (error) {
      console.error('Error loading premium meal plans:', error);
    }
  };

  const loadPremiumEducationalContent = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/premium-educational-content`);
      if (response.ok) {
        const data = await response.json();
        setPremiumData(prev => ({ ...prev, educationalContent: data }));
      }
    } catch (error) {
      console.error('Error loading premium educational content:', error);
    }
  };

  const submitCheckin = async () => {
    console.log('🚀 submitCheckin called!', { userProfile: !!userProfile, message: checkinMessage });
    if (!userProfile || !checkinMessage.trim()) return;
    
    try {
      setCheckinLoading(true);
      console.log('📡 Making API call to:', `${BACKEND_URL}/api/premium-checkin`);
      const response = await fetch(`${BACKEND_URL}/api/premium-checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userProfile.user_id,
          message: checkinMessage
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setPremiumData(prev => ({ ...prev, lastCheckin: data }));
        setCheckinMessage('');
        alert('Your message has been sent to Denise!');
      } else {
        alert('Error sending message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting checkin:', error);
      alert('Error sending message. Please try again.');
    } finally {
      setCheckinLoading(false);
    }
  };



  const renderPremium = () => {
    if (!userProfile) return <div className="tab-content">Please create a profile first.</div>;
    
    return (
      <div className="tab-content">
        <div className="premium-header">
          <h2>Premium Features</h2>
          <span className="premium-badge">👑 PREMIUM</span>
        </div>
        
        {!userPremiumStatus?.premium_active ? (
          <div className="premium-access-denied">
            <h3>Unlock Premium Features</h3>
            <p>Upgrade to Premium to access advanced analytics, personalized meal plans, check-ins with Denise, and exclusive content.</p>
            <div className="premium-features-preview">
              <div className="feature-preview"><BarChart3 size={15} strokeWidth={2.2} className="fp-icon" /> Advanced Analytics Dashboard</div>
              <div className="feature-preview"><FileText size={15} strokeWidth={2.2} className="fp-icon" /> Detailed Progress Reports (PDF)</div>
              <div className="feature-preview"><Pill size={15} strokeWidth={2.2} className="fp-icon" /> Personalized Meal Plans & Recipes</div>
              <div className="feature-preview"><Heart size={15} strokeWidth={2.2} className="fp-icon" /> Personal Check-ins with Denise</div>
              <div className="feature-preview"><GraduationCap size={15} strokeWidth={2.2} className="fp-icon" /> Premium Educational Content</div>
            </div>
            <button 
              className="btn-primary upgrade-btn"
              onClick={() => setShowUpgradeModal(true)}
            >
              Upgrade to Premium
            </button>
          </div>
        ) : (
          <div className="premium-content">
            {/* Premium Analytics Section */}
            <div className="premium-section">
              <h3>📊 Advanced Analytics Dashboard</h3>
              {premiumData.analytics ? (
                <div className="analytics-detailed">
                  <div className="analytics-row">
                    <div className="analytics-card">
                      <h4>Weight Trends</h4>
                      <div className="trend-details">
                        <div className="trend-item">
                          <span className="trend-label">7-Day Average:</span>
                          <span className="trend-value">{premiumData.analytics.weight_trends?.["7_day_average"]?.toFixed(1) || 0} lbs</span>
                        </div>
                        <div className="trend-item">
                          <span className="trend-label">30-Day Trend:</span>
                          <span className="trend-value">{prettyAnalyticsValue(premiumData.analytics.weight_trends?.["30_day_trend"])}</span>
                        </div>
                        <div className="trend-item">
                          <span className="trend-label">Weekly Rate:</span>
                          <span className="trend-value">{premiumData.analytics.weight_trends?.rate_per_week?.toFixed(1) || 0} lbs/week</span>
                        </div>
                        <div className="trend-item">
                          <span className="trend-label">Projected Goal Date:</span>
                          <span className="trend-value">{prettyAnalyticsValue(premiumData.analytics.weight_trends?.projected_goal_date)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="analytics-card">
                      <h4>Nutrition Patterns</h4>
                      <div className="nutrition-scores">
                        <div className="score-item">
                          <span className="metric-label">Protein Consistency</span>
                          <div className="score-bar">
                            <div className="score-fill" style={{width: `${premiumData.analytics.nutrition_patterns?.protein_consistency || 0}%`}}></div>
                            <span className="score-text">{Math.round(premiumData.analytics.nutrition_patterns?.protein_consistency || 0)}%</span>
                          </div>
                        </div>
                        <div className="score-item">
                          <span className="metric-label">Meal Timing</span>
                          <div className="score-bar">
                            <div className="score-fill" style={{width: `${premiumData.analytics.nutrition_patterns?.meal_timing_score || 0}%`}}></div>
                            <span className="score-text">{Math.round(premiumData.analytics.nutrition_patterns?.meal_timing_score || 0)}%</span>
                          </div>
                        </div>
                        <div className="score-item">
                          <span className="metric-label">Hydration Score</span>
                          <div className="score-bar">
                            <div className="score-fill" style={{width: `${premiumData.analytics.nutrition_patterns?.hydration_score || 0}%`}}></div>
                            <span className="score-text">{Math.round(premiumData.analytics.nutrition_patterns?.hydration_score || 0)}%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="analytics-row">
                    <div className="analytics-card">
                      <h4>Behavioral Insights</h4>
                      <div className="pattern-insights">
                        <div className="pattern-item">
                          <strong>Best Eating Time:</strong>
                          <span>{prettyAnalyticsValue(premiumData.analytics.behavioral_insights?.best_eating_time, 'Analyzing...')}</span>
                        </div>
                        <div className="pattern-item">
                          <strong>Most Successful Foods:</strong>
                          <span>{(premiumData.analytics.behavioral_insights?.highest_success_meals || []).filter(m => m && m !== 'insufficient_data').join(', ') || 'Keep tracking to discover patterns'}</span>
                        </div>
                        <div className="pattern-item">
                          <strong>Mood-Weight Correlation:</strong>
                          <span>{prettyAnalyticsValue(premiumData.analytics.mood_correlation?.mood_weight_correlation)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="analytics-card">
                      <h4>Data Summary</h4>
                      <div className="pattern-insights">
                        <div className="pattern-item">
                          <strong>Weight Entries:</strong>
                          <span>{premiumData.analytics.data_points_analyzed?.weight_entries || 0}</span>
                        </div>
                        <div className="pattern-item">
                          <strong>Food Entries:</strong>
                          <span>{premiumData.analytics.data_points_analyzed?.food_entries || 0}</span>
                        </div>
                        <div className="pattern-item">
                          <strong>Mood Entries:</strong>
                          <span>{premiumData.analytics.data_points_analyzed?.mood_entries || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="improvement-suggestions">
                    <h4>💡 Personalized Recommendations</h4>
                    <div className="suggestions-list">
                      {premiumData.analytics.behavioral_insights?.improvement_areas?.map((area, index) => (
                        <div key={index} className="suggestion-item">
                          <span className="suggestion-icon">🎯</span>
                          <span className="suggestion-text">Focus on improving {prettyAnalyticsValue(area).toLowerCase()}</span>
                        </div>
                      )) || [
                        <div key="default" className="suggestion-item">
                          <span className="suggestion-icon">📈</span>
                          <span className="suggestion-text">Keep tracking consistently to unlock personalized insights</span>
                        </div>
                      ]}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="analytics-loading">
                  <button 
                    className="btn-primary"
                    onClick={async () => {
                      try {
                        console.log('🔄 Loading premium analytics...');
                        const response = await fetch(`${BACKEND_URL}/api/premium-analytics/${userProfile.user_id}`);
                        if (response.ok) {
                          const data = await response.json();
                          console.log('✅ Premium analytics loaded:', data);
                          setPremiumData(prev => ({...prev, analytics: data}));
                        } else {
                          alert('Unable to load analytics. Please try again.');
                        }
                      } catch (error) {
                        console.error('❌ Analytics loading error:', error);
                        alert('Connection error. Please try again.');
                      }
                    }}
                  >
                    Load Advanced Analytics
                  </button>
                </div>
              )}
            </div>
            
            {/* Meal Plans Section */}
            <div className="premium-section">
              <h3>🍽️ Personalized Meal Plans</h3>
              {premiumData.mealPlans ? (
                <div className="meal-plans-grid">
                  {premiumData.mealPlans.personalized_plans?.map((plan, index) => (
                    <div key={index} className="meal-plan-card">
                      <h4 className="plan-title">{plan.title}</h4>
                      <p className="plan-description">{plan.description}</p>
                      <div className="plan-details">
                        <span>📅 {plan.duration_days} days</span>
                        <span>🔥 {plan.daily_calories} cal/day</span>
                        <span>💪 {plan.daily_protein}g protein/day</span>
                      </div>
                      <div className="sample-meals">
                        <strong>Sample Day:</strong>
                        <ul>
                          <li>Breakfast: {plan.sample_day?.breakfast?.meal} ({plan.sample_day?.breakfast?.calories} cal)</li>
                          <li>Lunch: {plan.sample_day?.lunch?.meal} ({plan.sample_day?.lunch?.calories} cal)</li>
                          <li>Dinner: {plan.sample_day?.dinner?.meal} ({plan.sample_day?.dinner?.calories} cal)</li>
                        </ul>
                      </div>
                      <button 
                        className="btn-secondary"
                        onClick={() => {
                          const ingredients = plan.shopping_list?.join('\n• ') || 'No shopping list available';
                          alert(`🛒 SHOPPING LIST - ${plan.title}

📋 INGREDIENTS NEEDED:
• ${ingredients}

💡 MEAL PREP TIPS:
- Shop fresh ingredients twice weekly
- Pre-portion proteins for easy cooking
- Keep backup frozen vegetables on hand
- Prepare sauces and dressings in advance

📱 Save this list to your phone for grocery shopping!`);
                        }}
                      >
                        View Shopping List
                      </button>
                    </div>
                  )) || []}
                </div>
              ) : (
                <div className="meal-plans-loading">
                  <button 
                    className="btn-primary"
                    onClick={async () => {
                      try {
                        console.log('🔄 Loading personalized meal plans...');
                        const response = await fetch(`${BACKEND_URL}/api/premium-meal-plans/${userProfile.user_id}`);
                        if (response.ok) {
                          const data = await response.json();
                          console.log('✅ Meal plans loaded:', data);
                          setPremiumData(prev => ({...prev, mealPlans: data}));
                        } else {
                          alert('Unable to load meal plans. Please try again.');
                        }
                      } catch (error) {
                        console.error('❌ Meal plans loading error:', error);
                        alert('Connection error. Please try again.');
                      }
                    }}
                  >
                    Load Personalized Meal Plans
                  </button>
                </div>
              )}
            </div>
            
            {/* Check-in with Denise Section */}
            <div className="premium-section">
              <h3>💬 Personal Check-in with Denise</h3>
              <div className="checkin-container">
                <div className="checkin-intro">
                  <p>Share your thoughts, challenges, or victories with Denise St. Charles. Get personalized support from someone who truly understands your journey.</p>
                </div>
                
                <div className="checkin-form">
                  <textarea
                    className="form-textarea"
                    placeholder="How are you feeling today? Share what's on your mind - your struggles, victories, questions, or just how your day went..."
                    value={checkinMessage}
                    onChange={(e) => setCheckinMessage(e.target.value)}
                    rows="4"
                  />
                  <button 
                    className="btn-primary"
                    onClick={submitCheckin}
                    disabled={!checkinMessage.trim() || checkinLoading}
                  >
                    {checkinLoading ? 'Connecting with Denise...' : 'Send Message to Denise'}
                  </button>
                </div>
                
                {premiumData.lastCheckin && (
                  <div className="last-checkin">
                    <h4>Latest Response from Denise:</h4>
                    <div className="denise-response">
                      <p>{premiumData.lastCheckin.response}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Premium Educational Content */}
            <div className="premium-section">
              <h3>📚 Premium Educational Content</h3>
              {premiumData.educationalContent ? (
                <div className="education-modules">
                  <div className="exclusive-resources" style={{marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px'}}>
                    <h4>📚 Exclusive Resources - Available Now</h4>
                    <div className="resources-grid" style={{display: 'grid', gap: '10px'}}>
                      <button 
                        className="btn-secondary"
                        onClick={async () => {
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/premium-recipes`);
                            const data = await response.json();
                            
                            if (response.ok) {
                              const recipeContent = `🍽️ GASTRIC SLEEVE RECIPE COLLECTION
${data.total_recipes} Tested Recipes with Exact Portions

═══════════════════════════════

RECIPE CATEGORIES:
• Breakfast: ${data.categories.breakfast} recipes
• Lunch: ${data.categories.lunch} recipes  
• Dinner: ${data.categories.dinner} recipes
• Snacks: ${data.categories.snacks} recipes

${data.gastric_sleeve_focus}

═══════════════════════════════

ALL RECIPES:

${data.recipes.map(recipe => `
${recipe.name} (${recipe.category})
Calories: ${recipe.calories} | Protein: ${recipe.protein}g | Carbs: ${recipe.carbs}g | Fat: ${recipe.fat}g
Prep Time: ${recipe.prep_time}

Ingredients: ${recipe.ingredients.join(', ')}

Instructions:
${recipe.instructions.map((step, i) => `${i + 1}. ${step}`).join('\n')}

Gastric Sleeve Notes: ${recipe.gastric_sleeve_notes}
`).join('\n═══════════════════════════════\n')}

🎉 Complete collection of ${data.total_recipes} recipes for gastric sleeve success!`;
                              
                              const popup = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
                              popup.document.write(`
                                <html>
                                  <head>
                                    <title>Recipe Collection - Gastric Buddy Premium</title>
                                    <style>
                                      body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
                                      pre { white-space: pre-wrap; font-size: 14px; }
                                    </style>
                                  </head>
                                  <body>
                                    <pre>${recipeContent}</pre>
                                  </body>
                                </html>
                              `);
                              popup.document.close();
                            } else {
                              alert('Error loading recipes. Please try again.');
                            }
                          } catch (error) {
                            console.error('Error fetching recipes:', error);
                            alert('Error loading recipes. Please check your connection.');
                          }
                        }}
                      >
                        📖 Recipe Collection (${userProfile?.isPremium ? '60+ Recipes' : 'Premium Required'})
                      </button>
                      
                      <button 
                        className="btn-secondary"
                        onClick={async () => {
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/premium-meal-planning-templates`);
                            const data = await response.json();
                            
                            if (response.ok && data.weekly_templates) {
                              const planningContent = `📅 MEAL PLANNING TEMPLATES
Weekly & Monthly Planning Made Easy

═══════════════════════════════

COMPLETE WEEKLY TEMPLATES:

${data.weekly_templates.map(template => `
📋 ${template.title}
${template.description}

COMPLETE 7-DAY MEAL PLAN:

${data.weekly_templates && data.weekly_templates[0] && data.weekly_templates[0].daily_plans ? 
Object.entries(data.weekly_templates[0].daily_plans).map(([day, dayPlan]) => `
${day.toUpperCase()}:
${dayPlan && Object.entries(dayPlan).map(([mealTime, mealDetails]) => `
  ${mealTime.replace(/_/g, ' ').toUpperCase()}: ${mealDetails.meal}
  • Calories: ${mealDetails.calories} | Protein: ${mealDetails.protein}g
  • Prep Time: ${mealDetails.prep_time}
  • Ingredients: ${mealDetails.ingredients.join(', ')}
  • Notes: ${mealDetails.notes}
`).join('')}
`).join('') : 'Complete daily meal plans available in your meal planning system'}

COMPLETE SHOPPING LIST:
${template.shopping_list ? template.shopping_list.map(item => `• ${item}`).join('\n') : 'Shopping lists provided in your meal planning templates'}

PREP TIPS:
${template.prep_tips ? template.prep_tips.map(tip => `• ${tip}`).join('\n') : 'Prep tips provided in your meal planning templates'}
`).join('\n═══════════════════════════════\n')}

MONTHLY TEMPLATES:
${data.monthly_templates ? data.monthly_templates.map(template => `
📅 ${template.title}
${template.description}

Weekly Themes:
• Week 1: ${template.week_1_theme}
• Week 2: ${template.week_2_theme}  
• Week 3: ${template.week_3_theme}
• Week 4: ${template.week_4_theme}

Weekly Prep Schedule:
${Object.entries(template.weekly_prep_schedule).map(([day, tasks]) => `
${day.toUpperCase()}: 
${tasks.map(task => `  • ${task}`).join('\n')}
`).join('')}
`).join('\n') : 'Additional monthly templates coming soon!'}

SPECIAL OCCASION TEMPLATES:
${data.special_occasion_templates ? data.special_occasion_templates.map(template => `
🎉 ${template.title} (${template.occasion})
${template.description}

${template.thanksgiving_menu ? `Thanksgiving Menu:
• Protein: ${template.thanksgiving_menu.protein}
• Sides: ${template.thanksgiving_menu.sides}
• Total Portion: ${template.thanksgiving_menu.total_portion}
• Notes: ${template.thanksgiving_menu.notes}` : ''}

${template.strategies ? `Restaurant Strategies:
${template.strategies.map(strategy => `• ${strategy}`).join('\n')}` : ''}
`).join('\n') : 'Special occasion templates available in your meal planning system!'}

COMPLETE PLANNING TOOLS:

Portion Guides by Recovery Phase:
${data.planning_tools && data.planning_tools.portion_guides ? Object.entries(data.planning_tools.portion_guides).map(([period, portion]) => `• ${period.replace(/_/g, ' ')}: ${portion}`).join('\n') : 'Portion guides included in your meal planning system'}

Timing Schedules:
${data.planning_tools && data.planning_tools.timing_schedules ? Object.entries(data.planning_tools.timing_schedules).map(([aspect, timing]) => `• ${aspect.replace(/_/g, ' ')}: ${timing}`).join('\n') : 'Timing schedules included in your meal planning system'}

Prep Strategies:
${data.planning_tools && data.planning_tools.prep_strategies ? data.planning_tools.prep_strategies.map(strategy => `• ${strategy}`).join('\n') : 'Prep strategies included in your meal planning system'}

🎉 Complete meal planning system - No more guesswork!`;
                              
                              const popup = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
                              popup.document.write(`
                                <html>
                                  <head>
                                    <title>Meal Planning Templates - Gastric Buddy Premium</title>
                                    <style>
                                      body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
                                      pre { white-space: pre-wrap; font-size: 14px; }
                                    </style>
                                  </head>
                                  <body>
                                    <pre>${planningContent}</pre>
                                  </body>
                                </html>
                              `);
                              popup.document.close();
                            } else {
                              alert('Error loading meal planning templates. Please try again.');
                            }
                          } catch (error) {
                            console.error('Error fetching meal templates:', error);
                            alert('Error loading meal planning templates. Please check your connection.');
                          }
                        }}
                      >
                        📋 Meal Planning Templates
                      </button>
                      
                      <button 
                        className="btn-secondary"
                        onClick={async () => {
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/premium-supplement-guide`);
                            const data = await response.json();
                            
                            if (response.ok && data.supplement_schedules) {
                              const supplementContent = `💊 SUPPLEMENT TIMING GUIDE
Maximize Absorption & Avoid Interactions

═══════════════════════════════

COMPLETE DAILY SUPPLEMENT SCHEDULE:

${Object.entries(data.supplement_schedules.daily_routine).map(([timingKey, schedule]) => `
📅 ${timingKey.replace(/_/g, ' ').toUpperCase()} - ${schedule.time}
${schedule.supplements.map(supp => `
• ${supp.name}: ${supp.dosage}
  Form: ${supp.form}
  Frequency: ${supp.frequency}
  Why: ${supp.reason}
  ${supp.notes ? `Special Notes: ${supp.notes}` : ''}
`).join('')}
`).join('')}

═══════════════════════════════

ALL CRITICAL INTERACTIONS TO AVOID:
${data.critical_interactions.avoid_combinations.map(interaction => `
❌ NEVER COMBINE: ${interaction.supplement_1} + ${interaction.supplement_2}
Problem: ${interaction.interaction}
Solution: ${interaction.solution}
Severity Level: ${interaction.severity}
`).join('')}

ALL BENEFICIAL COMBINATIONS:
${data.critical_interactions.beneficial_combinations.map(combo => `
✅ GREAT COMBO: ${combo.supplement_1} + ${combo.supplement_2}
Benefit: ${combo.benefit}
How to Take: ${combo.recommendation}
Timing: ${combo.timing}
`).join('')}

═══════════════════════════════

QUICK REFERENCE & DAILY CHECKLIST:

Daily Checklist:
${data.quick_reference_charts && data.quick_reference_charts.daily_checklist ? 
data.quick_reference_charts.daily_checklist.map(item => `${item}`).join('\n') : 'Daily supplement checklist available in your guide'}

Emergency Protocols:
${data.quick_reference_charts && data.quick_reference_charts.emergency_protocols ?
data.quick_reference_charts.emergency_protocols.map(protocol => `
• Scenario: ${protocol.scenario}
• Action: ${protocol.action}
• Note: ${protocol.note}
`).join('') : 'Emergency protocols available in your guide'}

═══════════════════════════════

COMPLETE MONITORING SCHEDULE:
${data.monitoring_schedule.laboratory_tests.map(test => `
🩺 ${test.test}
How Often: ${test.frequency}
What It Monitors: ${Array.isArray(test.monitors) ? test.monitors.join(', ') : test.monitors || 'Not specified'}
${test.target_range ? `Target Range: ${test.target_range}` : ''}
${test.target_ranges ? `Target Ranges: ${test.target_ranges}` : ''}
${test.notes ? `Important Notes: ${test.notes}` : ''}
`).join('')}

═══════════════════════════════

═══════════════════════════════

YOUR DAILY SUPPLEMENT CHECKLIST:
${data.quick_reference_charts && data.quick_reference_charts.daily_checklist ? 
data.quick_reference_charts.daily_checklist.join('\n') : 'Daily checklist available in your supplement guide'}

EMERGENCY PROTOCOLS:
${data.quick_reference_charts && data.quick_reference_charts.emergency_protocols ?
data.quick_reference_charts.emergency_protocols.map(protocol => `
🚨 If ${protocol.scenario}:
Action: ${protocol.action}
Note: ${protocol.note}
`).join('') : 'Emergency protocols available in your supplement guide'}

🎉 Complete supplement mastery - Never guess about supplements again!`;
                              // Force deployment update
                              
                              const popup = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
                              popup.document.write(`
                                <html>
                                  <head>
                                    <title>Supplement Timing Guide - Gastric Buddy Premium</title>
                                    <style>
                                      body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
                                      pre { white-space: pre-wrap; font-size: 14px; }
                                    </style>
                                  </head>
                                  <body>
                                    <pre>${supplementContent}</pre>
                                  </body>
                                </html>
                              `);
                              popup.document.close();
                            } else {
                              alert('Error loading supplement guide. Please try again.');
                            }
                          } catch (error) {
                            console.error('Error fetching supplement guide:', error);
                            alert('Error loading supplement guide. Please check your connection.');
                          }
                        }}
                      >
                        💊 Supplement Timing Guide
                      </button>
                    </div>
                  </div>
                  
                  {premiumData.educationalContent.educational_modules?.map((module, index) => (
                    <div key={index} className="education-card">
                      <h4 className="module-title">{module.title}</h4>
                      <p className="module-description">{module.description}</p>
                      <div className="module-meta">
                        <span>📖 {module.lessons} lessons</span>
                        <span>⏱️ {module.duration}</span>
                        <span>📊 {module.level}</span>
                      </div>
                      <div className="module-lessons">
                        <strong>Topics covered:</strong>
                        <ul>
                          {module.topics?.slice(0, 3).map((topic, i) => (
                            <li key={i}>{topic}</li>
                          ))}
                        </ul>
                      </div>
                      <button 
                        className="btn-secondary"
                        onClick={async () => {
                          let fullContent = '';
                          
                          if (module.module_id === 'advanced_nutrition') {
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/premium-advanced-nutrition`);
                              const data = await response.json();
                              
                              if (response.ok && data.lessons) {
                                fullContent = `📚 ${data.module_title}
${data.total_lessons} Lessons - ${data.duration}

═══════════════════════════════

ALL ${data.total_lessons} LESSONS:

${data.lessons.map(lesson => `
LESSON ${lesson.lesson_id.toUpperCase()}: ${lesson.title}
Week: ${lesson.week} | Duration: ${lesson.duration}

Learning Objectives:
${lesson.objectives.map(obj => `• ${obj}`).join('\n')}

${lesson.content.introduction}

Key Concepts:
${lesson.content.key_concepts.map(concept => `• ${concept}`).join('\n')}

${lesson.content.practical_application ? `Practical Application:
${lesson.content.practical_application.daily_macro_targets ? `Daily Macro Targets:
• Month 1-3: Protein: ${lesson.content.practical_application.daily_macro_targets.month_1_3.protein}, Carbs: ${lesson.content.practical_application.daily_macro_targets.month_1_3.carbs}, Fat: ${lesson.content.practical_application.daily_macro_targets.month_1_3.fat}
• Month 4-6: Protein: ${lesson.content.practical_application.daily_macro_targets.month_4_6.protein}, Carbs: ${lesson.content.practical_application.daily_macro_targets.month_4_6.carbs}, Fat: ${lesson.content.practical_application.daily_macro_targets.month_4_6.fat}
• Maintenance: Protein: ${lesson.content.practical_application.daily_macro_targets.maintenance.protein}, Carbs: ${lesson.content.practical_application.daily_macro_targets.maintenance.carbs}, Fat: ${lesson.content.practical_application.daily_macro_targets.maintenance.fat}` : ''}
${lesson.content.practical_application.meal_timing ? `Meal Timing: ${lesson.content.practical_application.meal_timing}` : ''}
${lesson.content.practical_application.hydration ? `Hydration: ${lesson.content.practical_application.hydration}` : ''}` : ''}

${lesson.content.common_mistakes ? `Common Mistakes to Avoid:
${lesson.content.common_mistakes.map(mistake => `• ${mistake}`).join('\n')}` : ''}

${lesson.content.action_steps ? `Action Steps:
${lesson.content.action_steps.map(step => `• ${step}`).join('\n')}` : ''}
`).join('\n═══════════════════════════════\n')}

COMPLETE PROGRAM OVERVIEW:
${Object.entries(data.week_summaries).map(([week, summary]) => `${week.toUpperCase()}: ${summary}`).join('\n')}

RESOURCES INCLUDED:
• ${data.resources.meal_planning_templates}
• ${data.resources.tracking_tools}
• ${data.resources.reference_guides}

🎉 Complete ${data.total_lessons}-lesson Advanced Nutrition Mastery program!`;
                              } else {
                                fullContent = "Error loading Advanced Nutrition content. Please try again.";
                              }
                            } catch (error) {
                              console.error('Error fetching advanced nutrition:', error);
                              fullContent = "Error loading Advanced Nutrition content. Please check your connection.";
                            }
                          
                          } else if (module.module_id === 'mindful_eating') {
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/premium-mindful-eating`);
                              const data = await response.json();
                              
                              if (response.ok && data.lessons) {
                                fullContent = `🧠 ${data.module_title}
${data.total_lessons} Lessons - ${data.duration}

Approach: ${data.approach}

═══════════════════════════════

ALL ${data.total_lessons} LESSONS:

${data.lessons.map(lesson => `
LESSON ${lesson.lesson_id.toUpperCase()}: ${lesson.title}
Week: ${lesson.week} | Duration: ${lesson.duration}

Learning Objectives:
${lesson.learning_objectives ? lesson.learning_objectives.map(obj => `• ${obj}`).join('\n') : 'Transform your relationship with food'}

${lesson.content.introduction}

${lesson.content.eating_triggers ? `EATING TRIGGERS:
Emotional Triggers:
${Object.entries(lesson.content.eating_triggers.emotional_triggers).map(([trigger, description]) => `• ${trigger.toUpperCase()}: ${description}`).join('\n')}

Environmental Triggers:
${Object.entries(lesson.content.eating_triggers.environmental_triggers).map(([trigger, description]) => `• ${trigger.toUpperCase()}: ${description}`).join('\n')}` : ''}

${lesson.content.mindful_eating_principles ? `MINDFUL EATING PRINCIPLES:
${Object.entries(lesson.content.mindful_eating_principles).map(([principle, description]) => `• ${principle.toUpperCase()}: ${description}`).join('\n')}` : ''}

${lesson.content.the_mindful_meal_process ? `MINDFUL MEAL PROCESS:
${Object.entries(lesson.content.the_mindful_meal_process).map(([phase, details]) => `
${phase.toUpperCase().replace(/_/g, ' ')}:
Steps: ${details.steps.join(', ')}
Time: ${details.time}
`).join('')}` : ''}

${lesson.content.emotional_eating_cycle ? `EMOTIONAL EATING CYCLE:
${Object.entries(lesson.content.emotional_eating_cycle).map(([stage, description]) => `${stage}: ${description}`).join(' → ')}` : ''}

${lesson.content.breaking_the_cycle ? `BREAKING THE CYCLE:
${Object.entries(lesson.content.breaking_the_cycle).map(([step, description]) => `• ${step.toUpperCase()}: ${description}`).join('\n')}` : ''}

${lesson.content.alternative_coping_strategies ? `ALTERNATIVE COPING STRATEGIES:
${Object.entries(lesson.content.alternative_coping_strategies).map(([emotion, strategies]) => `For ${emotion.toUpperCase()}: ${strategies.join(', ')}`).join('\n')}` : ''}

${lesson.content.hunger_assessment ? `HUNGER ASSESSMENT:
Physical Hunger Signs: ${lesson.content.hunger_assessment.physical_hunger_signs.join(', ')}
Emotional Hunger Signs: ${lesson.content.hunger_assessment.emotional_hunger_signs.join(', ')}
Hunger Scale: ${lesson.content.hunger_assessment.hunger_scale}` : ''}

${lesson.content.brain_science ? `BRAIN SCIENCE:
${Object.entries(lesson.content.brain_science).map(([concept, description]) => `• ${concept.replace(/_/g, ' ').toUpperCase()}: ${description}`).join('\n')}` : ''}

${lesson.content.practical_exercises ? `PRACTICAL EXERCISES:
${lesson.content.practical_exercises.map(exercise => `• ${exercise}`).join('\n')}` : ''}
`).join('\n═══════════════════════════════\n')}

COMPLETE TOOLS & RESOURCES:
Guided Meditations:
${data.tools_and_resources.guided_meditations.map(meditation => `• ${meditation}`).join('\n')}

Worksheets:
${data.tools_and_resources.worksheets.map(worksheet => `• ${worksheet}`).join('\n')}

Recommended Apps:
${data.tools_and_resources.apps_recommended.map(app => `• ${app}`).join('\n')}

🎉 Complete ${data.total_lessons}-lesson psychological transformation program!`;
                              } else {
                                fullContent = "Error loading Mindful Eating content. Please try again.";
                              }
                            } catch (error) {
                              console.error('Error fetching mindful eating:', error);
                              fullContent = "Error loading Mindful Eating content. Please check your connection.";
                            }
                          
                          } else if (module.module_id === 'exercise_progression') {
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/premium-exercise-progression`);
                              const data = await response.json();
                              
                              if (response.ok && data.lessons) {
                                fullContent = `💪 ${data.module_title}
${data.total_lessons} Lessons - ${data.duration}

${data.medical_disclaimer}

═══════════════════════════════

ALL ${data.total_lessons} LESSONS:

${data.lessons.map(lesson => `
LESSON ${lesson.lesson_id.toUpperCase()}: ${lesson.title}
Week: ${lesson.week} | Duration: ${lesson.duration}

${lesson.clearance_requirements ? `⚠️ CLEARANCE REQUIRED: ${lesson.clearance_requirements}` : ''}
${lesson.critical_importance ? `🚨 CRITICAL: ${lesson.critical_importance}` : ''}

${lesson.content.introduction}

${lesson.content.timeline_guidelines ? `TIMELINE GUIDELINES:
${Object.entries(lesson.content.timeline_guidelines).map(([phase, details]) => `
${phase.replace(/_/g, ' ').toUpperCase()}:
• Activities: ${details.activities.join(', ')}
• Intensity: ${details.intensity}
• Duration: ${details.duration}
• Goals: ${details.goals}
`).join('')}` : ''}

${lesson.content.muscle_preservation_strategies ? `MUSCLE PRESERVATION STRATEGIES:
${Object.entries(lesson.content.muscle_preservation_strategies).map(([strategy, details]) => `
${strategy.replace(/_/g, ' ').toUpperCase()}:
• Frequency: ${details.frequency}
• Focus: ${details.focus}
• Progression: ${details.progression}
• Exercises: ${Array.isArray(details.exercises) ? details.exercises.join(', ') : details.exercises || 'Not specified'}
`).join('')}

${lesson.content.muscle_preservation_strategies.protein_timing ? `Protein Timing:
${Object.entries(lesson.content.muscle_preservation_strategies.protein_timing).map(([timing, amount]) => `• ${timing.replace(/_/g, ' ')}: ${amount}`).join('\n')}` : ''}` : ''}

${lesson.content.beginner_workout_plan ? `BEGINNER WORKOUT PLAN:
${Object.entries(lesson.content.beginner_workout_plan).map(([week, details]) => `
${week.replace(/_/g, ' ').toUpperCase()}:
• Exercises: ${Array.isArray(details.exercises) ? details.exercises.join(', ') : details.exercises || 'Not specified'}
• Sets/Reps: ${details.sets_reps || 'Not specified'}
• Rest: ${details.rest || 'Not specified'}
`).join('')}` : ''}

${lesson.content.safety_protocols && Array.isArray(lesson.content.safety_protocols) ? `SAFETY PROTOCOLS:
${lesson.content.safety_protocols.map(protocol => `• ${protocol}`).join('\n')}` : ''}
`).join('\n═══════════════════════════════\n')}

${data.workout_library ? `COMPLETE WORKOUT LIBRARY:
• ${data.workout_library.beginner_routines || 'Beginner routines available'}
• ${data.workout_library.intermediate_routines || 'Intermediate routines available'}
• ${data.workout_library.advanced_routines || 'Advanced routines available'}` : 'Workout library available in your program'}

${data.tracking_tools ? `TRACKING TOOLS:
• ${data.tracking_tools.progress_photos || 'Progress photo guidance'}
• ${data.tracking_tools.measurement_charts || 'Measurement tracking charts'}
• ${data.tracking_tools.strength_logs || 'Strength progress logs'}` : 'Tracking tools provided in your program'}

🎉 Complete ${data.total_lessons}-lesson Exercise Progression Mastery program!`;
                              } else {
                                fullContent = "Error loading Exercise Progression content. Please try again.";
                              }
                            } catch (error) {
                              console.error('Error fetching exercise progression:', error);
                              fullContent = "Error loading Exercise Progression content. Please check your connection.";
                            }
                          
                          } else if (module.module_id === 'denise_mentorship') {
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/premium-personal-growth`);
                              const data = await response.json();
                              
                              if (response.ok && data.lessons) {
                                fullContent = `✨ ${data.module_title}
${data.total_lessons} Lessons - ${data.duration}

${data.instructor_bio}

═══════════════════════════════

ALL ${data.total_lessons} LESSONS:

${data.lessons.map(lesson => `
LESSON ${lesson.lesson_id.toUpperCase()}: ${lesson.title}
Week: ${lesson.week} | Duration: ${lesson.duration}

Personal Story: ${lesson.personal_story}

${lesson.content.introduction}

${lesson.content.mindset_shifts ? `MINDSET SHIFTS:
${lesson.content.mindset_shifts.map(shift => `• ${shift}`).join('\n')}` : ''}

${lesson.content.denise_insights ? `DENISE'S INSIGHTS:
${lesson.content.denise_insights.map(insight => `"${insight}"`).join('\n')}` : ''}

${lesson.content.confidence_pillars ? `CONFIDENCE PILLARS:
${Object.entries(lesson.content.confidence_pillars).map(([pillar, details]) => `
${pillar.replace(/_/g, ' ').toUpperCase()}:
Definition: ${details.definition}
Denise's Example: ${details.denise_example}
Practice: ${details.practice}
`).join('')}` : ''}

${lesson.content.confidence_challenges ? `CONFIDENCE CHALLENGES:
${lesson.content.confidence_challenges.map(challenge => `• ${challenge}`).join('\n')}` : ''}

${lesson.content.perfectionism_traps ? `PERFECTIONISM TRAPS:
${lesson.content.perfectionism_traps.map(trap => `• ${trap}`).join('\n')}` : ''}

${lesson.content.healthy_alternatives ? `HEALTHY ALTERNATIVES:
${lesson.content.healthy_alternatives.map(alternative => `• ${alternative}`).join('\n')}` : ''}

${lesson.content.denise_wisdom ? `DENISE'S WISDOM:
${lesson.content.denise_wisdom.map(wisdom => `"${wisdom}"`).join('\n')}` : ''}

${lesson.content.practical_exercises ? `PRACTICAL EXERCISES:
${lesson.content.practical_exercises.map(exercise => `• ${exercise}`).join('\n')}` : ''}

${lesson.content.reflection_questions ? `REFLECTION QUESTIONS:
${lesson.content.reflection_questions.map(question => `• ${question}`).join('\n')}` : ''}
`).join('\n═══════════════════════════════\n')}

COMPLETE BONUS CONTENT:
Denise's Video Messages:
${data.bonus_content.denise_video_messages.map(message => `• ${message}`).join('\n')}

Community Access: ${data.bonus_content.community_access}

Monthly Live Calls: ${data.bonus_content.monthly_live_calls}

🎉 Complete ${data.total_lessons}-lesson personal transformation journey with Denise!`;
                              } else {
                                fullContent = "Error loading Personal Growth content. Please try again.";
                              }
                            } catch (error) {
                              console.error('Error fetching personal growth:', error);
                              fullContent = "Error loading Personal Growth content. Please check your connection.";
                            }
                          }
                          
                          // Show the full content
                          const popup = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
                          popup.document.write(`
                            <html>
                              <head>
                                <title>${module.title} - Full Course</title>
                                <style>
                                  body { 
                                    font-family: Arial, sans-serif; 
                                    padding: 20px; 
                                    line-height: 1.6; 
                                    max-width: 800px; 
                                    margin: 0 auto;
                                  }
                                  pre { 
                                    white-space: pre-wrap; 
                                    font-size: 14px;
                                    background: #f5f5f5;
                                    padding: 20px;
                                    border-radius: 8px;
                                    border-left: 4px solid #007cba;
                                  }
                                  .close-btn {
                                    position: fixed;
                                    top: 20px;
                                    right: 20px;
                                    background: #007cba;
                                    color: white;
                                    border: none;
                                    padding: 10px 15px;
                                    border-radius: 5px;
                                    cursor: pointer;
                                  }
                                </style>
                              </head>
                              <body>
                                <button class="close-btn" onclick="window.close()">Close</button>
                                <pre>${fullContent}</pre>
                              </body>
                            </html>
                          `);
                          popup.document.close();
                        }}
                      >
                        Start Module
                      </button>
                    </div>
                  )) || []}
                </div>
              ) : (
                <div className="education-loading">
                  <button 
                    className="btn-primary"
                    onClick={async () => {
                      try {
                        console.log('🔄 Loading premium educational content...');
                        const response = await fetch(`${BACKEND_URL}/api/premium-content`);
                        if (response.ok) {
                          const data = await response.json();
                          console.log('✅ Educational content loaded:', data);
                          setPremiumData(prev => ({...prev, educationalContent: data}));
                        } else {
                          alert('Unable to load educational content. Please try again.');
                        }
                      } catch (error) {
                        console.error('❌ Educational content loading error:', error);
                        alert('Connection error. Please try again.');
                      }
                    }}
                  >
                    Load Educational Content
                  </button>
                </div>
              )}
            </div>
            
            {/* Progress Reports */}
            <div className="premium-section">
              <h3>📋 Detailed Progress Reports</h3>
              <div className="report-generation">
                <p>Generate comprehensive PDF reports with detailed analytics, trends, and recommendations.</p>
                <div className="report-options">
                  <label>
                    <input 
                      type="radio" 
                      name="reportPeriod" 
                      value="30_days"
                      checked={reportPeriod === '30_days'}
                      onChange={(e) => setReportPeriod(e.target.value)}
                    />
                    Last 30 Days
                  </label>
                  <label>
                    <input 
                      type="radio" 
                      name="reportPeriod" 
                      value="90_days"
                      checked={reportPeriod === '90_days'}
                      onChange={(e) => setReportPeriod(e.target.value)}
                    />
                    Last 90 Days
                  </label>
                  <label>
                    <input 
                      type="radio" 
                      name="reportPeriod" 
                      value="6_months"
                      checked={reportPeriod === '6_months'}
                      onChange={(e) => setReportPeriod(e.target.value)}
                    />
                    Last 6 Months
                  </label>
                </div>
                <button 
                  className="btn-primary generate-report-btn"
                  onClick={async () => {
                    try {
                      setReportLoading(true);
                      console.log('🔄 Generating progress report...');
                      
                      // Call the backend to generate the actual PDF report
                      const response = await fetch(`${BACKEND_URL}/api/premium-progress-report/${userProfile.user_id}`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          period: reportPeriod
                        })
                      });
                      
                      if (response.ok) {
                        const data = await response.json();
                        console.log('✅ Progress report generated:', data);
                        
                        // Show success message with summary
                        alert(`📊 PROGRESS REPORT GENERATED!

✅ Your comprehensive progress report has been created successfully.

📊 Report includes:
• Weight analysis and trends
• Nutrition patterns and insights  
• Mood and wellness tracking
• Personalized recommendations
• Achievement highlights

📄 Ready to download your PDF report!`);
                        
                        // Auto-download the PDF after a brief delay
                        setTimeout(async () => {
                          try {
                            console.log('📄 Downloading PDF...');
                            const pdfResponse = await fetch(`${BACKEND_URL}/api/generate-pdf-report/${data.report_data.report_id}`);
                            
                            if (pdfResponse.ok) {
                              const blob = await pdfResponse.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.style.display = 'none';
                              a.href = url;
                              a.download = `gastric-buddy-progress-report-${reportPeriod}-${new Date().toISOString().split('T')[0]}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                              console.log('✅ PDF downloaded successfully!');
                            } else {
                              alert('PDF generated but download failed. Please try again.');
                            }
                          } catch (pdfError) {
                            console.error('❌ PDF download error:', pdfError);
                            alert('PDF generated but download failed. Please contact support.');
                          }
                        }, 1000);
                        
                      } else {
                        const errorText = await response.text();
                        console.error('❌ Report generation failed:', errorText);
                        alert('Failed to generate progress report. Please try again.');
                      }
                      
                    } catch (error) {
                      console.error('❌ Report generation error:', error);
                      alert('Connection error. Please try again.');
                    } finally {
                      setReportLoading(false);
                    }
                  }}
                  disabled={reportLoading}
                >
                  {reportLoading ? 'Generating Report...' : 'Generate Progress Report'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPrivacyPolicy = () => {
    if (!privacyPolicy) return <div>Loading...</div>;

    return (
      <div className="tab-content">
        <div className="legal-header">
          <h2 className="heading-2">{privacyPolicy.title}</h2>
          <p className="legal-updated">Last Updated: {privacyPolicy.last_updated}</p>
        </div>

        <div className="legal-content">
          {privacyPolicy.sections.map((section, index) => (
            <div key={index} className="legal-section-item">
              <div className="progress-card">
                <h3 className="heading-4">{section.title}</h3>
                <p className="legal-text">{section.content}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="legal-footer">
          <div className="progress-card">
            <h4 className="heading-4">Questions?</h4>
            <p className="body-medium">
              If you have any questions about this Privacy Policy, please contact us 
              through the Mood & Journal section or the About page.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderTermsOfService = () => {
    if (!termsOfService) return <div>Loading...</div>;

    return (
      <div className="tab-content">
        <div className="legal-header">
          <h2 className="heading-2">{termsOfService.title}</h2>
          <p className="legal-updated">Last Updated: {termsOfService.last_updated}</p>
        </div>

        <div className="legal-content">
          {termsOfService.sections.map((section, index) => (
            <div key={index} className="legal-section-item">
              <div className="progress-card">
                <h3 className="heading-4">{section.title}</h3>
                <p className="legal-text">{section.content}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="legal-footer">
          <div className="progress-card">
            <h4 className="heading-4">Contact</h4>
            <p className="body-medium">
              For questions about these Terms of Service, please contact us 
              through the app's About section.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderConsentModal = () => {
    const [consentForm, setConsentForm] = useState({
      privacy_policy_accepted: false,
      terms_accepted: false,
      health_data_consent: false,
      ai_analysis_consent: false
    });

    const handleConsentSubmit = () => {
      const allConsented = Object.values(consentForm).every(value => value === true);
      
      if (allConsented) {
        handleConsent(consentForm);
      } else {
        alert('Please accept all consent items to continue using Gastric Buddy.');
      }
    };

    if (!showConsentModal) return null;

    return (
      <div className="consent-modal-overlay">
        <div className="consent-modal">
          <div className="consent-modal-header">
            <img src={logo} alt="Gastric Buddy" className="app-logo-lg" />
            <h2 className="heading-3">Welcome to Gastric Buddy!</h2>
            <p className="body-medium">
              Before we begin your journey together, we need your consent to provide 
              you with personalized health insights and support.
            </p>
          </div>

          <div className="consent-modal-content">
            <div className="consent-item">
              <label className="consent-checkbox">
                <input 
                  type="checkbox" 
                  checked={consentForm.privacy_policy_accepted}
                  onChange={(e) => setConsentForm({...consentForm, privacy_policy_accepted: e.target.checked})}
                />
                <span className="checkmark"></span>
                I have read and agree to the Privacy Policy
              </label>
              <button 
                className="view-document-btn"
                onClick={() => setActiveTab('privacy')}
              >
                View Privacy Policy
              </button>
            </div>

            <div className="consent-item">
              <label className="consent-checkbox">
                <input 
                  type="checkbox"
                  checked={consentForm.terms_accepted}
                  onChange={(e) => setConsentForm({...consentForm, terms_accepted: e.target.checked})}
                />
                <span className="checkmark"></span>
                I have read and agree to the Terms of Service
              </label>
              <button 
                className="view-document-btn"
                onClick={() => setActiveTab('terms')}
              >
                View Terms of Service
              </button>
            </div>

            <div className="consent-item">
              <label className="consent-checkbox">
                <input 
                  type="checkbox"
                  checked={consentForm.health_data_consent}
                  onChange={(e) => setConsentForm({...consentForm, health_data_consent: e.target.checked})}
                />
                <span className="checkmark"></span>
                I consent to the collection and processing of my health data 
                (weight, nutrition, mood, journal entries) for app functionality
              </label>
            </div>

            <div className="consent-item">
              <label className="consent-checkbox">
                <input 
                  type="checkbox"
                  checked={consentForm.ai_analysis_consent}
                  onChange={(e) => setConsentForm({...consentForm, ai_analysis_consent: e.target.checked})}
                />
                <span className="checkmark"></span>
                I consent to sending food images to AI services for nutritional analysis 
                (images are processed but not stored)
              </label>
            </div>
          </div>

          <div className="consent-modal-footer">
            <p className="consent-note">
              <strong>Your privacy matters:</strong> We will never sell your personal data 
              or share your journal entries. All data is encrypted and secure.
            </p>
            <div className="consent-actions">
              <button 
                className="btn-primary"
                onClick={handleConsentSubmit}
              >
                I Agree - Start My Journey
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUpgradeModal = () => {
    if (!subscriptionPackages || !showUpgradeModal) return null;

    return (
      <div className="upgrade-modal-overlay">
        <div className="upgrade-modal">
          <div className="upgrade-modal-header">
            <h2 className="heading-3">Upgrade to Premium</h2>
            <button 
              className="close-btn"
              onClick={() => setShowUpgradeModal(false)}
            >
              ✕
            </button>
          </div>

          <div className="upgrade-content">
            <div className="upgrade-intro">
              <p className="body-medium">
                Unlock the full power of Gastric Buddy with unlimited AI food scanning, 
                advanced analytics, and exclusive content from Denise St. Charles.
              </p>
            </div>

            <div className="subscription-packages">
              {subscriptionPackages.packages.map((pkg, index) => {
                // Calculate discounted price if promo code is active
                let displayPrice = pkg.price;
                let originalPrice = null;
                
                if (promoDiscount && promoDiscount.discount_percent > 0) {
                  originalPrice = pkg.price;
                  displayPrice = pkg.price * (1 - promoDiscount.discount_percent / 100);
                  if (promoDiscount.discount_percent === 100) {
                    displayPrice = 0;
                  }
                }
                
                return (
                  <div key={pkg.id} className={`package-card ${index === 1 ? 'popular' : ''}`}>
                    {index === 1 && <div className="popular-badge">Most Popular</div>}
                    
                    <div className="package-header">
                      <h3 className="package-name">{pkg.name}</h3>
                      <div className="package-price">
                        {promoDiscount && originalPrice ? (
                          <>
                            <span className="price-original">${originalPrice}</span>
                            <span className="price">${displayPrice.toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="price">${pkg.price}</span>
                        )}
                        <span className="billing">/{pkg.interval}</span>
                      </div>
                      {promoDiscount && (
                        <div className="promo-applied">
                          {promoDiscount.description}
                        </div>
                      )}
                      {pkg.discount && (
                        <div className="savings-badge">{pkg.discount}</div>
                      )}
                    </div>

                    <div className="package-features">
                      {pkg.features.map((feature, idx) => (
                        <div key={idx} className="feature-item">
                          <span className="feature-check">✓</span>
                          <span className="feature-text">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button 
                      className={`btn-${index === 1 ? 'primary' : 'secondary'} package-btn`}
                      onClick={() => {
                        setShowUpgradeModal(false);
                        initiatePayment(pkg.id, promoDiscount);
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Processing...' : 
                       promoDiscount && promoDiscount.discount_percent === 100 ? 
                       'Start Free Trial' : 
                       `Choose ${pkg.name}`}
                    </button>
                  </div>
                );
              })}
            </div>
            
            {/* Promo Code Section */}
            <div className="promo-code-section">
              <div className="promo-code-input-container">
                <input
                  type="text"
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  className="promo-code-input"
                />
                <button
                  className="btn-secondary promo-apply-btn"
                  onClick={() => validatePromoCode(promoCode)}
                  disabled={promoValidating || !promoCode.trim()}
                >
                  {promoValidating ? 'Checking...' : 'Apply'}
                </button>
              </div>
              
              {promoDiscount && (
                <div className="promo-success">
                  ✅ Promo code applied: {promoDiscount.description}
                  <button 
                    className="promo-remove"
                    onClick={handlePromoRemove}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <div className="upgrade-benefits">
              <h4 className="heading-4">Why Upgrade?</h4>
              <div className="benefits-grid">
                <div className="benefit-item">
                  <span className="benefit-icon">🤖</span>
                  <span className="benefit-text">Unlimited AI food analysis</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">📊</span>
                  <span className="benefit-text">Advanced mood & progress analytics</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">💝</span>
                  <span className="benefit-text">Exclusive content from Denise</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">🎯</span>
                  <span className="benefit-text">Priority customer support</span>
                </div>
              </div>
            </div>

            <div className="upgrade-guarantee">
              <p className="guarantee-text">
                <strong>30-day money-back guarantee.</strong> If you're not completely satisfied, 
                we'll refund your subscription, no questions asked.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {showSplash && (
        <div className="gb-splash" data-testid="splash-screen">
          <div className="gb-splash-inner">
            <img src={logo} alt="Gastric Buddy" className="gb-splash-logo" />
            <div className="gb-splash-dots" aria-hidden="true">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      )}
      <div className="app">
      <header className="app-header">
        <img src={logo} alt="Gastric Buddy — Track. Plan. Progress." className="app-logo" />
        <h1 className="app-title">Gastric Buddy</h1>
        <p className="app-subtitle">by Denise St. Charles</p>
        <p className="app-tagline">Your post-surgery nutrition companion</p>
      </header>

      <nav className="bottom-nav" id="bottom-nav">
        {/* Row 1: Main features (5 tabs) */}
        <button 
          className={`nav-button ${activeTab === 'home' ? 'active' : ''}`}
          onClick={() => setActiveTab('home')}
          data-testid="nav-home"
        >
          <Home className="nav-icon" strokeWidth={2} />
          <span className="nav-label">Home</span>
        </button>
        <button 
          className={`nav-button ${activeTab === 'scan' ? 'active' : ''}`}
          onClick={() => setActiveTab('scan')}
          data-testid="nav-scan"
        >
          <Camera className="nav-icon" strokeWidth={2} />
          <span className="nav-label">Scan</span>
        </button>
        <button 
          className={`nav-button ${activeTab === 'vitamins' ? 'active' : ''}`}
          onClick={() => setActiveTab('vitamins')}
          data-testid="nav-vitamins"
        >
          <Pill className="nav-icon" strokeWidth={2} />
          <span className="nav-label">Vitamins</span>
        </button>
        <button 
          className={`nav-button ${activeTab === 'fluids' ? 'active' : ''}`}
          onClick={() => setActiveTab('fluids')}
          data-testid="nav-fluids"
        >
          <Droplet className="nav-icon" strokeWidth={2} />
          <span className="nav-label">Fluids</span>
        </button>
        <button 
          className={`nav-button ${activeTab === 'weight' ? 'active' : ''}`}
          onClick={() => setActiveTab('weight')}
          data-testid="nav-weight"
        >
          <Scale className="nav-icon" strokeWidth={2} />
          <span className="nav-label">Weight</span>
        </button>

        {/* Row 2: Secondary features */}
        <button 
          className={`nav-button ${activeTab === 'mood' ? 'active' : ''}`}
          onClick={() => setActiveTab('mood')}
          data-testid="nav-mood"
        >
          <Smile className="nav-icon" strokeWidth={2} />
          <span className="nav-label">Mood</span>
        </button>
        <button 
          className={`nav-button ${activeTab === 'tips' ? 'active' : ''}`}
          onClick={() => setActiveTab('tips')}
          data-testid="nav-tips"
        >
          <Lightbulb className="nav-icon" strokeWidth={2} />
          <span className="nav-label">Tips</span>
        </button>
        <button 
          className={`nav-button ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
          data-testid="nav-about"
        >
          <Info className="nav-icon" strokeWidth={2} />
          <span className="nav-label">About</span>
        </button>
        
        {/* Premium Tab - Only show for premium users */}
        {userPremiumStatus?.premium_active ? (
          <button 
            className={`nav-button premium-tab ${activeTab === 'premium' ? 'active' : ''}`}
            onClick={() => setActiveTab('premium')}
            data-testid="nav-premium"
          >
            <Gem className="nav-icon" strokeWidth={2} />
            <span className="nav-label">Premium</span>
          </button>
        ) : (
          <div className="nav-placeholder"></div>
        )}
        
        {/* Extra placeholder for grid alignment */}
        <div className="nav-placeholder"></div>
      </nav>

      <main className="app-main">
        {activeTab === 'home' && renderHome()}
        {activeTab === 'profile' && renderProfile()}
        {activeTab === 'scan' && renderScan()}
        {activeTab === 'vitamins' && renderVitamins()}
        {activeTab === 'fluids' && renderFluids()}
        {activeTab === 'weight' && renderWeight()}
        {activeTab === 'mood' && renderMood()}
        {activeTab === 'tips' && renderTips()}
        {activeTab === 'about' && renderAbout()}
        {activeTab === 'premium' && renderPremium()}
        {activeTab === 'privacy' && renderPrivacyPolicy()}
        {activeTab === 'terms' && renderTermsOfService()}
      </main>
      
      {renderConsentModal()}
      {showUpgradeModal && renderUpgradeModal()}
      </div>

      {/* Streak Milestone Toast (Premium) */}
      {streakToast.show && streakToast.events.length > 0 && (
        <div className="streak-toast" data-testid="streak-toast" role="status" aria-live="polite">
          <button
            className="streak-toast-close"
            onClick={() => setStreakToast({ show: false, events: [] })}
            aria-label="Dismiss"
          >
            <X size={14} strokeWidth={2.5} />
          </button>
          <Sparkles size={22} strokeWidth={2.2} className="streak-toast-icon" />
          <div className="streak-toast-body">
            <div className="streak-toast-title">You hit a new milestone!</div>
            {streakToast.events.map((ev, i) => (
              <div key={i} className="streak-toast-item">
                <Award size={14} strokeWidth={2.2} /> {ev.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Share Preview Modal */}
      {sharePreview.open && (
        <div
          className="share-preview-overlay"
          onClick={() => setSharePreview({ open: false, dataUrl: null, blob: null })}
          data-testid="share-preview-overlay"
        >
          <div className="share-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-preview-header">
              <span className="share-preview-title">Preview your share card</span>
              <button
                className="share-preview-close"
                onClick={() => setSharePreview({ open: false, dataUrl: null, blob: null })}
                aria-label="Close"
                data-testid="share-preview-close"
              >
                <X size={18} strokeWidth={2.2} />
              </button>
            </div>
            <div className="share-preview-body">
              {sharePreview.dataUrl && (
                <img src={sharePreview.dataUrl} alt="Progress share preview" className="share-preview-image" data-testid="share-preview-image" />
              )}
            </div>
            <div className="share-preview-actions">
              <button
                className="btn-secondary share-btn"
                onClick={downloadShareImage}
                data-testid="share-preview-download"
              >
                <Download size={15} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Download
              </button>
              <button
                className="btn-primary share-btn"
                onClick={nativeShareImage}
                data-testid="share-preview-share"
              >
                <Share2 size={15} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Share
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Digest — auto-shows once per ISO week */}
      {weeklyDigest.open && userProfile && (() => {
        const phase = getPhaseInfo(userProfile.surgery_date);
        const note = getWeeklyNote(phase.phaseKey);
        const lost = weightHistory?.weight_lost_lbs ?? 0;
        const rawEntries = Array.isArray(weightHistory?.entries) ? weightHistory.entries : [];
        const entries7d = rawEntries
          .filter(e => (e?.notes || '').toLowerCase() !== 'initial weight entry')
          .filter(e => {
            if (!e.date) return false;
            const d = new Date(e.date);
            const cutoff = new Date();
            cutoff.setDate(cutoff.getDate() - 7);
            return d >= cutoff;
          });
        const weekChange = entries7d.length >= 2
          ? (entries7d[0].weight_lbs - entries7d[entries7d.length - 1].weight_lbs)
          : 0;
        return (
          <div
            className="weekly-digest-overlay"
            onClick={() => setWeeklyDigest({ open: false })}
            data-testid="weekly-digest-overlay"
          >
            <div className="weekly-digest-modal" onClick={(e) => e.stopPropagation()}>
              <div className="weekly-digest-header">
                <img src={logo} alt="Gastric Buddy" className="weekly-digest-logo" />
                <button
                  className="weekly-digest-close"
                  onClick={() => setWeeklyDigest({ open: false })}
                  aria-label="Close"
                  data-testid="weekly-digest-close"
                >
                  <X size={18} strokeWidth={2.2} />
                </button>
              </div>
              <div className="weekly-digest-body">
                <div className="weekly-digest-eyebrow"><Calendar size={12} strokeWidth={2.5} /> Weekly Digest</div>
                <h2 className="weekly-digest-title">Hi {userProfile.name || 'friend'}, here's your week.</h2>
                <p className="weekly-digest-phase">{phase.label} • Week {phase.weeksSince || 0}</p>

                <div className="digest-stats">
                  <div className="digest-stat">
                    <Scale size={18} strokeWidth={2.2} />
                    <div className="digest-stat-num">
                      {weekChange > 0 ? `-${Math.abs(weekChange).toFixed(1)}` : weekChange < 0 ? `+${Math.abs(weekChange).toFixed(1)}` : '—'}
                    </div>
                    <div className="digest-stat-label">lbs this week</div>
                  </div>
                  <div className="digest-stat">
                    <Trophy size={18} strokeWidth={2.2} />
                    <div className="digest-stat-num">{lost.toFixed(1)}</div>
                    <div className="digest-stat-label">total lbs lost</div>
                  </div>
                  <div className="digest-stat">
                    <Pill size={18} strokeWidth={2.2} />
                    <div className="digest-stat-num">{streaks?.vitamin_streak_days ?? 0}</div>
                    <div className="digest-stat-label">vitamin streak</div>
                  </div>
                  <div className="digest-stat">
                    <Droplet size={18} strokeWidth={2.2} />
                    <div className="digest-stat-num">{streaks?.fluid_streak_days ?? 0}</div>
                    <div className="digest-stat-label">fluid streak</div>
                  </div>
                </div>

                <div className="digest-note">
                  <Heart size={14} strokeWidth={2.2} />
                  <p>"{note}"</p>
                </div>

                <div className="digest-actions">
                  <button
                    className="btn-secondary digest-btn"
                    onClick={() => { setWeeklyDigest({ open: false }); shareProgressImage(); }}
                    data-testid="digest-share-btn"
                  >
                    <Share2 size={15} strokeWidth={2.2} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Share this week
                  </button>
                  <button
                    className="btn-primary digest-btn"
                    onClick={() => setWeeklyDigest({ open: false })}
                    data-testid="digest-continue-btn"
                  >
                    Let's go
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

export default App;